import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import partnerApiService from "../../api/PartnerApiService";
import uniquenessCheckApiService from "../../api/UniquenessCheckApiService";
import { useCategories } from "../../contexts/CategoriesContext";
import { getItemsByIndustry } from "../../data/categoryData";
import {
  ArrowLeft, ArrowRight, Check, Copy, Save, Building2, ShieldCheck,
  Layers, Users, Upload, CreditCard, FileText, ChevronDown, ChevronUp, X, Plus,
  Trash2, AlertCircle, CheckCircle2, CloudUpload, Sparkles, KeyRound,
  Info, RotateCcw, Mail, MessageSquare, Wand2, Lock, AlertTriangle, Flag,
} from "lucide-react";

/* ══════════════════════════════════════════════════════════════
    CONSTANTS
══════════════════════════════════════════════════════════════ */

/** Visible on local dev only — hide on production/live hosts. */
const SHOW_FINAL_PAYLOAD_BUTTON =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

/** Visible outside production to speed up QA form testing. */
// const SHOW_FILL_TEST_DATA_BUTTON = process.env.NODE_ENV !== "production";
const SHOW_FILL_TEST_DATA_BUTTON=false;

const STEPS = [
  {
    id: 1, label: "Company Info", icon: Building2,
    subtitle: "Basic identity and location",
    tip: "We use your company identity and address to start verifying the business before it appears on InfoBoxx.",
  },
  {
    id: 2, label: "Contact Details", icon: Users,
    subtitle: "Primary representative",
    tip: "This is the person InfoBoxx will contact for verification, onboarding updates, content coordination, and lead routing.",
  },
  {
    id: 3, label: "Verification", icon: ShieldCheck,
    subtitle: "Business documents and KYC",
    tip: "Provide your registration details, TIN, company documents, and one valid ID for the responsible representative.",
  },
  {
    id: 4, label: "Services", icon: Layers,
    subtitle: "Categories, capacity, content, and leads",
    tip: "Your selections determine where the provider is listed, which users are matched, and how leads are delivered.",
  },
  {
    id: 5, label: "Commercial", icon: CreditCard,
    subtitle: "Sponsorship and payment model",
    tip: "Commercial preferences help InfoBoxx align provider visibility, shared leads, sponsorships, and payment setup.",
  },
  {
    id: 6, label: "Review & Submit", icon: FileText,
    subtitle: "Review, sign, and submit",
    tip: "Once submitted, the application enters admin review. Approved providers can then publish content, receive leads, and go live.",
  },
];

const COMPANY_TYPE_OPTIONS = [
  { value: "Law Firm", label: "Law Firm" },
  { value: "Consultancy", label: "Consultancy" },
  { value: "Corporate Entity", label: "Corporate Entity" },
  { value: "Sole Proprietor", label: "Sole Proprietor" },
];

const REGIONS = [
  "Greater Accra", "Ashanti", "Western", "Eastern", "Central",
  "Northern", "Upper East", "Upper West", "Volta", "Oti",
  "Bono", "Bono East", "Ahafo", "Savannah", "North East", "Western North",
];

const REGION_CITIES = {
  "Greater Accra": ["Accra", "Tema", "Madina", "Adenta", "Kasoa", "Ga West"],
  Ashanti: ["Kumasi", "Obuasi", "Ejisu", "Mampong", "Konongo", "Bekwai"],
  Western: ["Sekondi-Takoradi", "Tarkwa", "Axim", "Prestea", "Shama", "Half Assini"],
  Eastern: ["Koforidua", "Nsawam", "Akosombo", "Nkawkaw", "Suhum", "Aburi"],
  Central: ["Cape Coast", "Kasoa", "Winneba", "Mankessim", "Elmina", "Dunkwa"],
  Northern: ["Tamale", "Savelugu", "Yendi", "Walewale", "Bimbilla", "Gushegu"],
  "Upper East": ["Bolgatanga", "Bawku", "Navrongo", "Zuarungu", "Paga", "Sandema"],
  "Upper West": ["Wa", "Lawra", "Tumu", "Jirapa", "Nadowli", "Nandom"],
  Volta: ["Ho", "Keta", "Hohoe", "Anloga", "Aflao", "Kpando"],
  Oti: ["Dambai", "Jasikan", "Nkwanta", "Kete Krachi", "Biakoye", "Chinderi"],
  Bono: ["Sunyani", "Berekum", "Dormaa Ahenkro", "Wenchi", "Nsawkaw", "Duayaw Nkwanta"],
  "Bono East": ["Techiman", "Kintampo", "Atebubu", "Nkoranza", "Prang", "Yeji"],
  Ahafo: ["Goaso", "Kenyasi", "Hwidiem", "Bechem", "Mim", "Acherensua"],
  Savannah: ["Damongo", "Bole", "Salaga", "Sawla", "Daboya", "Larabanga"],
  "North East": ["Nalerigu", "Walewale", "Gambaga", "Bunkpurugu", "Chereponi", "Yagaba"],
  "Western North": ["Sefwi Wiawso", "Bibiani", "Aowin", "Juaboso", "Bodi", "Akontombra"],
};

const SERVICE_CATEGORY_OPTIONS = [
  "Legal",
  "Health",
  "Real Estate",
  "Careers",
  "Betting & Gaming",
  "Nutrition",
];

const SERVICE_CATEGORY_TO_INDUSTRY_ID = {
  Legal: "legal",
  Health: "health",
  "Real Estate": "real-estate",
  Careers: "careers",
  "Betting & Gaming": "betting-gaming",
  Nutrition: "nutrition",
};

const SPECIFIC_SERVICE_OPTIONS = [
  "Consultation",
  "Documentation support",
  "Application processing",
  "Advisory session",
  "Client onboarding",
  "Content sponsorship",
];

const SERVICE_MODES = ["Physical", "Remote", "Both"];
const LANGUAGE_OPTIONS = ["English", "Twi", "Ga", "Ewe", "Hausa", "Dagbani", "French"];
const CONTACT_METHODS = ["Email", "Phone", "WhatsApp"];
const RESPONSE_TIME_OPTIONS = ["Within 1 hour", "Within 24 hours"];
const COMMERCIAL_INTEREST_OPTIONS = ["Exclusive Sponsorship", "Shared Leads"];
const PAYMENT_METHODS = ["MoMo", "Bank Transfer"];
const YES_NO_OPTIONS = ["Yes", "No"];
const ID_TYPE_OPTIONS = ["National ID (Ghana Card)", "Passport", "Driver's License"];
const DIGITAL_CAPABILITY_OPTIONS = ["Fully Digital", "Hybrid", "Primarily Physical"];
const CUSTOMER_SEGMENT_OPTIONS = ["Individuals", "SMEs", "Corporates", "Government", "NGOs"];
const CONTENT_TYPE_OPTIONS = ["Articles", "Guides", "FAQs", "Videos", "Product Sheets", "Newsletters"];
const ENGAGEMENT_MODEL_OPTIONS = [
  "Commission-based",
  "Subscription",
  "Sponsorship",
  "Lead purchase",
  "Hybrid",
];
const REGULATOR_OPTIONS = ["NIC", "BoG", "SEC", "NPRA", "Other"];
const LICENSE_CLASS_OPTIONS = ["Life", "General", "Composite", "Microinsurance", "Other"];

/** Values must match backend `form.commChannels`. */
const COMM_CHANNEL_OPTIONS = [
  {
    value: "Email",
    label: "Email",
    description: "Official notices, lead alerts, and compliance updates in your inbox.",
  },
  {
    value: "SMS",
    label: "SMS",
    description: "Concise text messages for urgent reminders and time-sensitive updates.",
  },
  {
    value: "WhatsApp",
    label: "WhatsApp",
    description: "Fast coordination with your partner success team on mobile chat.",
  },
];

const REQUIRED_DOCS = [
  {
    id: "cert_incorporation",
    label: "Certificate of Incorporation",
    hint: "Required · PDF/JPEG/PNG · max 5 MB",
    accept: ".pdf,.jpg,.jpeg,.png",
    allowedMimeTypes: ["application/pdf", "image/jpeg", "image/png"],
  },
  {
    id: "cert_commence",
    label: "Certificate of Commencement",
    hint: "Required · PDF/JPEG/PNG · max 5 MB",
    accept: ".pdf,.jpg,.jpeg,.png",
    allowedMimeTypes: ["application/pdf", "image/jpeg", "image/png"],
  },
  {
    id: "contract_agreement",
    label: "Contract Agreement",
    hint: "Optional · PDF/JPEG/PNG · max 5 MB",
    accept: ".pdf,.jpg,.jpeg,.png",
    allowedMimeTypes: ["application/pdf", "image/jpeg", "image/png"],
  },
];

/*
 * Document entry shapes stored in form.documents:
 *   { type: "base64", name: "file.pdf", data: "data:application/pdf;base64,..." }  ← newly uploaded
 *   { type: "url",    name: "file.pdf", url:  "https://cdn.example.com/..."      }  ← loaded from server
 */

/** Convert a browser File into a base64 document entry. */
const fileToDocEntry = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ type: "base64", name: file.name, data: reader.result });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const isBase64Entry = (entry) => entry?.type === "base64";
const isUrlEntry = (entry) => entry?.type === "url";
const isUploadEntry = (entry) => isBase64Entry(entry) || isUrlEntry(entry);

const useEntryPreviewUrl = (entry) => {
  if (isBase64Entry(entry)) return entry.data;
  if (isUrlEntry(entry)) return entry.url;
  return null;
};

/**
 * Normalize a single ID-card image value from the backend.
 * Accepts: URL string, base64 entry, url entry, or null/undefined.
 */
const normalizeContactIdImage = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    const name = value.split("/").pop() || "id_image";
    return { type: "url", name, url: value };
  }
  if (value?.type === "base64" || value?.type === "url") return value;
  return null;
};

/**
 * Normalise a contact object's idFront/idBack fields.
 */
const normalizeContactFromBackend = (c) => {
  if (!c || typeof c !== "object") return c;
  return {
    ...c,
    idFront: normalizeContactIdImage(c.idFront),
    idBack:  normalizeContactIdImage(c.idBack),
  };
};

/**
 * Normalise a single document value into a local upload entry.
 * Accepts URL strings, data-URLs, or { type, url|data } objects.
 */
const normalizeDocValue = (id, value, nameHint = "") => {
  if (!value) return null;
  if (typeof value === "string") {
    const name = nameHint || value.split("/").pop() || id;
    if (value.startsWith("data:")) return { type: "base64", name, data: value };
    return { type: "url", name, url: value };
  }
  if (value?.type === "base64" || value?.type === "url") return value;
  const nested = value.url || value.data || value.path || value.file || null;
  if (typeof nested === "string") {
    return normalizeDocValue(id, nested, value.name || nameHint);
  }
  if (value.uploaded && (value.url || value.data)) {
    return normalizeDocValue(id, value.url || value.data, value.name || nameHint);
  }
  return null;
};

/**
 * Normalise the `documents` map from the backend.
 * Supports:
 *   - object map `{ cert_incorporation: "https://..." }`
 *   - array of `{ id|key|doc_id, url|data }` (backend sometimes stores [])
 */
const normalizeDocumentsFromBackend = (raw) => {
  if (!raw) return {};
  const result = {};

  if (Array.isArray(raw)) {
    raw.forEach((item) => {
      if (!item || typeof item !== "object") return;
      const id = item.id || item.doc_id || item.docId || item.key || item.type;
      if (!id) return;
      const entry = normalizeDocValue(id, item.url || item.data || item.path || item.file || item, item.name);
      if (entry) result[id] = entry;
    });
    return result;
  }

  if (typeof raw !== "object") return {};
  for (const [id, value] of Object.entries(raw)) {
    const entry = normalizeDocValue(id, value);
    if (entry) result[id] = entry;
  }
  return result;
};

/** Prefer non-empty incoming values when merging draft forms. */
const pickFilled = (...candidates) => {
  for (const value of candidates) {
    if (value == null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    return value;
  }
  return candidates.find((v) => v !== undefined) ?? "";
};

const textOrEmpty = (value) => (value == null ? "" : String(value));

/* ══════════════════════════════════════════════════════════════
   INITIAL FORM STATE
══════════════════════════════════════════════════════════════ */

const INITIAL_FORM = {
  legalName: "", tradingName: "", companyType: "", companyTypeCustom: "", yearIncorporated: "",
  countryOfIncorporation: "Ghana", registrationNumber: "", tin: "", businessDescription: "",
  registeredAddress: "", operationalAddress: "", city: "", region: "", country: "Ghana",
  gpsLocation: "", corporatePhone: "", corporateEmail: "", websiteUrl: "",
  primaryRegulator: "", primaryRegulatorCustom: "", licenseNumber: "", licenseClass: "", licenseIssueDate: "",
  licenseExpiryDate: "", complianceAccepted: false,
  primaryCategories: [], subCategories: [], specificServices: [], customerSegments: [], coverageAreas: [],
  digitalCapability: "", yearsOfExperience: "", numberOfStaff: "", serviceMode: "", languagesSpoken: [],
  willProvideContent: "", contentTypes: [], hasExistingContent: "", contentUploadCapability: "",
  preferredContactMethod: "", acceptsInstantLeads: "", responseTimeSla: "", appointmentAvailability: "",
  commercialInterests: [], budgetRange: "", paymentMethod: "",
  logo: null,
  primaryContact: { fullName: "", jobTitle: "", email: "", phone: "", alternatePhone: "", idType: "", idNumber: "", idFront: null, idBack: null },
  secondaryContacts: [],
  documents: {},
  engagementModel: "", commChannels: [],
  declarationAccuracy: false, consentDataProcessing: false, acceptTerms: false,
  digitalSignatureName: "", digitalSignatureDate: new Date().toISOString().slice(0, 10),
};

const EMPTY_SECONDARY_CONTACT = {
  fullName: "",
  jobTitle: "",
  email: "",
  phone: "",
  alternatePhone: "",
  idType: "",
  idNumber: "",
  idFront: null,
  idBack: null,
};

/**
 * Dev / QA: fills every field except file uploads (company logo, ID images, mandatory docs).
 * Uses real option values so validation passes once uploads are added.
 */
function buildTestPartnerForm() {
  const subCats = ["Health Insurance Plans", "General Medical Consultations (Online/Offline)"];
  const today = new Date();
  const inOneYear = new Date(today);
  inOneYear.setFullYear(inOneYear.getFullYear() + 1);
  const iso = (d) => d.toISOString().slice(0, 10);

  return {
    ...INITIAL_FORM,
    legalName: "Demo Partner Limited",
    tradingName: "Demo Partner",
    companyType: "Insurance",
    companyTypeCustom: "",
    yearIncorporated: "2015",
    countryOfIncorporation: "Ghana",
    registrationNumber: "CS-DEMO-10001",
    tin: "C000000000099",
    businessDescription:
      "Automated test fill for partner onboarding. Replace with real information before submitting.",
    registeredAddress: "45 Independence Avenue, Ridge",
    operationalAddress: "45 Independence Avenue, Ridge",
    city: "Accra",
    region: "Greater Accra",
    country: "Ghana",
    gpsLocation: "GA-453-2211",
    corporatePhone: "+233302000000",
    corporateEmail: "hello@demo-partner.test",
    websiteUrl: "https://example.com",
    primaryRegulator: "NIC",
    primaryRegulatorCustom: "",
    licenseNumber: "NIC/LIC/DEMO-2044",
    licenseClass: "Life",
    licenseIssueDate: iso(today),
    licenseExpiryDate: iso(inOneYear),
    complianceAccepted: true,
    primaryCategories: ["insurances"],
    subCategories: subCats,
    customerSegments: ["Individuals", "SMEs"],
    coverageAreas: ["Greater Accra", "Ashanti"],
    digitalCapability: "Hybrid",
    logo: null,
    primaryContact: {
      fullName: "Ama Demo",
      jobTitle: "Head of Partnerships",
      email: "ama.demo@example.com",
      phone: "+233241111222",
      idType: "Ghana Card",
      idFront: null,
      idBack: null,
    },
    secondaryContacts: [
      {
        fullName: "Kwesi Demo",
        jobTitle: "Compliance Officer",
        email: "kwesi.demo@example.com",
        phone: "+233249333444",
        idType: "Ghana Card",
        idFront: null,
        idBack: null,
      },
    ],
    documents: {},
    engagementModel: "Commission-based",
    commChannels: ["Email", "SMS"],
    appointmentAvailability: "Yes",
    declarationAccuracy: true,
    consentDataProcessing: true,
    acceptTerms: true,
    digitalSignatureName: "Ama Demo",
    digitalSignatureDate: iso(today),
  };
}

/* ══════════════════════════════════════════════════════════════
   ANIMATIONS
══════════════════════════════════════════════════════════════ */

const pageVariants = {
  enter: (dir) => ({
    opacity: 0,
    x: dir > 0 ? 36 : -36,
    transition: { duration: 0.25, ease: [0.32, 0.72, 0, 1] },
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.35, ease: [0.32, 0.72, 0, 1] },
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir > 0 ? -36 : 36,
    transition: { duration: 0.25, ease: [0.32, 0.72, 0, 1] },
  }),
};

const overlayCardVariants = {
  enter: (dir) => ({
    opacity: 0,
    x: dir > 0 ? 48 : -48,
    transition: { duration: 0.4, ease: [0.32, 0.72, 0, 1] },
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.45, ease: [0.32, 0.72, 0, 1] },
  },
  exit: (dir) => ({
    opacity: 0,
    x: dir > 0 ? -48 : 48,
    transition: { duration: 0.35, ease: [0.32, 0.72, 0, 1] },
  }),
};

/* ══════════════════════════════════════════════════════════════
   REUSABLE FIELD COMPONENTS
══════════════════════════════════════════════════════════════ */

const Label = ({ children, required }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
    {children}
    {required && <span className="text-rose-400 ml-0.5 normal-case tracking-normal">*</span>}
  </label>
);

const FieldError = ({ msg }) =>
  msg ? (
    <p className="mt-1.5 text-xs text-rose-500 flex items-center gap-1 font-medium">
      <AlertCircle size={11} />
      {msg}
    </p>
  ) : null;

const Input = ({ label, required, error, className = "", ...props }) => (
  <div className={className}>
    {label && <Label required={required}>{label}</Label>}
    <input
      {...props}
      className={`w-full rounded-xl border px-4 py-3 text-sm text-gray-900 bg-white outline-none transition-all duration-200
        placeholder:text-gray-300 focus:ring-2 focus:ring-[#FFD221]/50 focus:border-[#FFD221]
        ${error ? "border-rose-300 bg-rose-50/60 focus:ring-rose-200 focus:border-rose-400" : "border-gray-200 hover:border-gray-300"}`}
    />
    <FieldError msg={error} />
  </div>
);

const Textarea = ({ label, required, error, className = "", ...props }) => (
  <div className={className}>
    {label && <Label required={required}>{label}</Label>}
    <textarea
      {...props}
      rows={3}
      className={`w-full rounded-xl border px-4 py-3 text-sm text-gray-900 bg-white outline-none resize-none transition-all duration-200
        placeholder:text-gray-300 focus:ring-2 focus:ring-[#FFD221]/50 focus:border-[#FFD221]
        ${error ? "border-rose-300 bg-rose-50/60" : "border-gray-200 hover:border-gray-300"}`}
    />
    <FieldError msg={error} />
  </div>
);

const Select = ({ label, required, error, children, className = "", ...props }) => (
  <div className={className}>
    {label && <Label required={required}>{label}</Label>}
    <div className="relative">
      <select
        {...props}
        className={`w-full appearance-none rounded-xl border px-4 py-3 pr-9 text-sm text-gray-900 bg-white outline-none transition-all duration-200
          focus:ring-2 focus:ring-[#FFD221]/50 focus:border-[#FFD221]
          ${error ? "border-rose-300 bg-rose-50/60" : "border-gray-200 hover:border-gray-300"}`}
      >
        {children}
      </select>
      <ChevronDown size={15} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </div>
    <FieldError msg={error} />
  </div>
);

/**
 * Input variant with an inline uniqueness-check status indicator.
 * checkStatus: "idle" | "checking" | "available" | "taken" | "error"
 */
const CheckedInput = ({ label, required, error, checkStatus = "idle", takenMsg, className = "", ...props }) => {
  const hasValue = String(props?.value ?? "").trim().length > 0;
  const effectiveCheckStatus = hasValue ? checkStatus : "idle";
  const isError = error || effectiveCheckStatus === "taken";
  const borderClass = isError
    ? "border-rose-300 bg-rose-50/60 focus:ring-rose-200 focus:border-rose-400"
    : effectiveCheckStatus === "available"
    ? "border-emerald-300 bg-emerald-50/30 focus:ring-emerald-100 focus:border-emerald-400"
    : "border-gray-200 hover:border-gray-300 focus:ring-[#FFD221]/50 focus:border-[#FFD221]";

  return (
    <div className={className}>
      {label && <Label required={required}>{label}</Label>}
      <div className="relative">
        <input
          {...props}
          className={`px-4 py-3 pr-10 w-full text-sm text-gray-900 bg-white rounded-xl border transition-all duration-200 outline-none placeholder:text-gray-300 focus:ring-2 ${borderClass}`}
        />
        <span className="flex absolute right-3 top-1/2 justify-center items-center -translate-y-1/2 pointer-events-none">
          {effectiveCheckStatus === "checking" && (
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-[#FFD221]" />
          )}
          {effectiveCheckStatus === "available" && <CheckCircle2 size={16} className="text-emerald-500" />}
          {effectiveCheckStatus === "taken"     && <X            size={16} className="text-rose-500" />}
          {effectiveCheckStatus === "error"     && <AlertTriangle size={16} className="text-amber-400" />}
        </span>
      </div>

      {/* Priority: form-level error > uniqueness status */}
      {error ? (
        <FieldError msg={error} />
      ) : effectiveCheckStatus === "checking" ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-400 animate-pulse">
          Checking availability…
        </p>
      ) : effectiveCheckStatus === "available" ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
          <CheckCircle2 size={11} />
          Available — not yet registered
        </p>
      ) : effectiveCheckStatus === "taken" ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-rose-500">
          <AlertCircle size={11} />
          {takenMsg || "Already registered to another account"}
        </p>
      ) : effectiveCheckStatus === "error" ? (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-amber-500">
          <AlertTriangle size={11} />
          Couldn't verify — you may continue and we'll confirm on submission
        </p>
      ) : null}
    </div>
  );
};

const SelectWithOther = ({
  label, required, error, value, onChange,
  customValue, onCustomChange,
  customLabel, customPlaceholder = "Please specify…",
  customError,
  children, className = "",
}) => (
  <div className={className}>
    <Select label={label} required={required} value={value} onChange={onChange} error={error}>
      {children}
    </Select>
    <AnimatePresence>
      {value === "Other" && (
        <motion.div
          key="other-input"
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: "auto", marginTop: 12 }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
          style={{ overflow: "hidden" }}
        >
          <Input
            label={customLabel ?? `Specify ${label}`}
            required
            placeholder={customPlaceholder}
            value={customValue}
            onChange={(e) => onCustomChange(e.target.value)}
            error={customError}
          />
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const Pill = ({ label, active, onClick, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-xs font-semibold transition-all duration-200 ${className} ${
      active
        ? "border-[#FFD221] bg-[#FFD221]/20 text-black shadow-sm"
        : "border-gray-200 bg-white text-gray-600 hover:border-[#FFD221]/60 hover:bg-[#FFD221]/10"
    }`}
  >
    {active && <Check size={10} strokeWidth={3} className="text-[#9a7b00] shrink-0" />}
    {label}
  </button>
);

const MultiSelect = ({
  label, required, options, selected, onToggle, error, className = "",
  optionsContainerClass = "", optionClassName = "", headerAction = null,
}) => (
  <div className={className}>
    {label ? (
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <p className="text-xs font-semibold tracking-wide text-gray-600 uppercase">
          {label}
          {required ? <span className="ml-0.5 normal-case tracking-normal text-rose-400">*</span> : null}
        </p>
        {headerAction}
      </div>
    ) : null}
    <div className={`mt-0.5 flex flex-wrap gap-2 ${optionsContainerClass}`}>
      {options.map((opt) => (
        (() => {
          const value = typeof opt === "string" ? opt : opt.value;
          const optLabel = typeof opt === "string" ? opt : opt.label;
          return (
            <Pill
              key={value}
              label={optLabel}
              active={selected.includes(value)}
              onClick={() => onToggle(value)}
              className={optionClassName}
            />
          );
        })()
      ))}
    </div>
    <FieldError msg={error} />
  </div>
);

const SingleSelectCards = ({
  id,
  label,
  required,
  options,
  selected,
  onSelect,
  error,
  className = "",
  optionsClassName = "grid grid-cols-1 gap-3 sm:grid-cols-3",
}) => (
  <div id={id} className={className}>
    {label && <Label required={required}>{label}</Label>}
    <div className={optionsClassName}>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onSelect(option)}
          className={`rounded-2xl border p-4 text-left text-sm font-black transition-all ${
            selected === option
              ? "border-[#FFD221] bg-[#FFD221]/20 text-black shadow-sm"
              : "border-gray-200 bg-white text-gray-600 hover:border-[#FFD221]/50"
          }`}
        >
          {option}
        </button>
      ))}
    </div>
    <FieldError msg={error} />
  </div>
);

const AddOtherChips = ({ label = "Add other", placeholder = "Type and press Add", values = [], onAdd, onRemove }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const addValues = () => {
    const nextValues = draft
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    if (!nextValues.length) return;
    nextValues.forEach(onAdd);
    setDraft("");
    setOpen(true);
  };

  return (
    <div className="mt-3 rounded-2xl border border-dashed border-gray-200 bg-gray-50/70 p-3">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-1.5 rounded-xl border border-[#FFD221]/50 bg-white px-3 py-2 text-xs font-bold text-[#9a7b00] transition hover:bg-[#FFD221]/10"
      >
        <Plus size={13} />
        {label}
      </button>

      {values.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {values.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => onRemove(value)}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#FFD221]/40 bg-[#FFD221]/15 px-3 py-1.5 text-xs font-semibold text-gray-800"
            >
              {value}
              <X size={11} />
            </button>
          ))}
        </div>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: "auto", marginTop: 12 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            style={{ overflow: "hidden" }}
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addValues();
                  }
                }}
                placeholder={placeholder}
                className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/40"
              />
              <button
                type="button"
                onClick={addValues}
                className="rounded-xl bg-[#FFD221] px-4 py-2 text-sm font-bold text-black transition hover:bg-[#c9a000]"
              >
                Add
              </button>
            </div>
            <p className="mt-2 text-[11px] text-gray-400">Separate multiple entries with commas.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CheckboxField = ({ label, checked, onChange }) => (
  <div
    className={`flex gap-3 items-start p-4 rounded-xl border cursor-pointer transition-all duration-200 ${
      checked ? "border-[#FFD221]/50 bg-[#FFD221]/[0.07]" : "border-gray-200 bg-white hover:border-gray-300"
    }`}
    onClick={onChange}
  >
    <div
      className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all duration-200 ${
        checked ? "bg-[#FFD221] border-[#FFD221]" : "border-gray-300"
      }`}
    >
      {checked && <Check size={10} className="text-black" strokeWidth={3} />}
    </div>
    <span className="text-sm leading-relaxed text-gray-700 select-none">{label}</span>
  </div>
);

/* ══════════════════════════════════════════════════════════════
    RE-SUBMISSION SUCCESS PAGE
══════════════════════════════════════════════════════════════ */
const RESUBMIT_TIMELINE = [
  {
    icon: CheckCircle2,
    label: "Corrections received",
    desc: "Your updated documents and information are securely stored.",
    accent: "#FFD221",
    delay: 0.3,
  },
  {
    icon: ShieldCheck,
    label: "Compliance re-review",
    desc: "Our team will assess the corrections within 2–5 business days.",
    accent: "#E5B80B",
    delay: 0.45,
  },
  {
    icon: KeyRound,
    label: "Decision notification",
    desc: "You'll receive an email at the address on file once a decision is made.",
    accent: "#FFD221",
    delay: 0.6,
  },
  {
    icon: Sparkles,
    label: "Go live on approval",
    desc: "Your dashboard credentials will be sent and your products activated.",
    accent: "#E5B80B",
    delay: 0.75,
  },
];

function ResubmissionSuccessPage({ companyName, email }) {
  return (
    <div className="min-h-screen bg-[#f7f9f8] flex flex-col">
      {/* top accent bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-[#FFD221] via-[#c9a000] to-black" />

      {/* nav strip */}
      <div className="flex justify-between items-center px-6 py-4 mx-auto w-full max-w-5xl">
        <Link to="/" className="flex gap-2 items-center group">
          <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400 group-hover:text-[#9a7b00] transition-colors">
            InfoBoxx
          </span>
        </Link>
      </div>

      {/* hero */}
      <div className="flex flex-col flex-1 justify-center items-center px-4 pt-6 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.32, 0.72, 0, 1] }}
          className="w-full max-w-2xl"
        >
          {/* card */}
          <div className="bg-white rounded-3xl shadow-[0_24px_80px_rgba(255,210,33,0.15)] border border-gray-100 overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[#FFD221] via-[#c9a000] to-black" />

            <div className="px-8 pt-10 pb-8 text-center">
              {/* animated icon */}
              <div className="flex relative justify-center mb-6">
                {[1, 2, 3].map((i) => (
                  <motion.div
                    key={i}
                    className="flex absolute inset-0 justify-center items-center"
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: [0, 0.35, 0], scale: [0.6, 1.6 + i * 0.25, 2.2 + i * 0.2] }}
                    transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.5, ease: "easeOut" }}
                  >
                    <div className="w-20 h-20 rounded-full border border-[#FFD221]/40" />
                  </motion.div>
                ))}
                <motion.div
                  initial={{ scale: 0, rotate: -15 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.15, type: "spring", stiffness: 240, damping: 18 }}
                  className="relative z-10 w-20 h-20 rounded-full bg-[#FFD221]/20 flex items-center justify-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 280, damping: 20 }}
                  >
                    <CheckCircle2 size={38} className="text-[#9a7b00]" strokeWidth={1.6} />
                  </motion.div>
                </motion.div>
              </div>

              {/* heading */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFD221]/20 border border-[#FFD221]/40 text-[11px] font-bold uppercase tracking-widest text-[#9a7b00] mb-4">
                  <Check size={10} strokeWidth={3} />
                  Corrections re-submitted
                </span>
                <h1 className="mb-2 text-2xl font-black leading-tight text-gray-900 sm:text-3xl">
                  {companyName ? `Thank you, ${companyName}!` : "Corrections Submitted!"}
                </h1>
                <p className="mx-auto max-w-md text-sm leading-relaxed text-gray-500">
                  {email
                    ? <>Your updated application is with our compliance team. We'll reach you at <strong className="text-gray-700">{email}</strong> with our decision.</>
                    : "Your updated application is with our compliance team. Expect a decision within 2–5 business days."
                  }
                </p>
              </motion.div>
            </div>

            {/* timeline */}
            <div className="px-8 pb-8">
              <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-4 text-center">What happens next</p>
              <div className="space-y-3">
                {RESUBMIT_TIMELINE.map(({ icon: Icon, label, desc, accent, delay }, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay, duration: 0.4, ease: "easeOut" }}
                    className="flex items-start gap-3.5 p-3.5 rounded-2xl bg-gray-50 border border-gray-100"
                  >
                    <div
                      className="flex justify-center items-center w-8 h-8 rounded-xl shrink-0"
                      style={{ background: accent + "18" }}
                    >
                      <Icon size={15} style={{ color: accent }} strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-gray-800">{label}</p>
                      <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">{desc}</p>
                    </div>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: delay + 0.2, type: "spring", stiffness: 260, damping: 20 }}
                      className="w-5 h-5 rounded-full bg-[#FFD221]/20 flex items-center justify-center shrink-0 mt-0.5"
                    >
                      <Check size={9} className="text-[#9a7b00]" strokeWidth={3} />
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="flex flex-col gap-3 px-8 pb-10 sm:flex-row"
            >
              <Link
                to="/"
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-5 rounded-2xl bg-[#FFD221] text-black text-sm font-bold hover:bg-[#c9a000] transition-all shadow-lg shadow-[#FFD221]/30 hover:-translate-y-0.5"
              >
                <ArrowLeft size={15} />
                Go Home
              </Link>
              {/* <Link
                to="/products"
                className="flex-1 inline-flex items-center justify-center gap-2 py-3 px-5 rounded-2xl bg-[#E5B80B]/10 border border-[#E5B80B]/30 text-[#9a7a00] text-sm font-bold hover:bg-[#E5B80B]/20 transition-all hover:-translate-y-0.5"
              >
                <Sparkles size={15} />
                Explore our products
                <ArrowRight size={15} />
              </Link> */}
            </motion.div>
          </div>

          {/* subtle footnote */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="text-center text-[11px] text-gray-400 mt-5"
          >
            Your application reference is secured. No further action is needed from you at this time.
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}

const ActionStatusModal = ({ open, status, title, message, details, code, steps, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!open) return null;
  const success = status === "success";

  const handleCopy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      {/* backdrop */}
      <motion.div
        className="absolute inset-0 bg-[#060f0c]/80 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.97 }}
        transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
        className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-[0_32px_80px_rgba(0,0,0,0.35)] max-h-[90vh] overflow-y-auto"
      >
        {/* colour bar */}
        <div className={`h-1 ${success
          ? "bg-gradient-to-r from-[#FFD221] via-[#c9a000] to-black"
          : "bg-gradient-to-r from-rose-500 to-rose-400"
        }`} />

        <div className="px-6 pt-6 pb-6">

          {/* ── Icon ── */}
          {success ? (
            <div className="flex justify-center mb-4">
              <div className="flex relative justify-center items-center w-14 h-14">
                {[1, 2].map((i) => (
                  <motion.div
                    key={i}
                    className="absolute inset-0 rounded-full border-2 border-[#FFD221]/40"
                    animate={{ scale: [1, 1.6 + i * 0.3], opacity: [0.5, 0] }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.4, ease: "easeOut" }}
                  />
                ))}
                <motion.div
                  className="w-14 h-14 rounded-full bg-[#FFD221]/20 flex items-center justify-center"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring", stiffness: 220, damping: 18 }}
                >
                  <motion.div
                    initial={{ scale: 0, rotate: -20 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.25, type: "spring", stiffness: 260, damping: 20 }}
                  >
                    <CheckCircle2 size={28} className="text-[#9a7b00]" strokeWidth={1.75} />
                  </motion.div>
                </motion.div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center mb-4">
              <motion.div
                className="flex justify-center items-center w-12 h-12 bg-rose-50 rounded-2xl"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 220, damping: 18 }}
              >
                <AlertCircle size={24} className="text-rose-500" />
              </motion.div>
            </div>
          )}

          {/* ── Title + message ── */}
          <motion.div
            className="mb-4 text-center"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18 }}
          >
            <h3 className="mb-1.5 text-lg font-black text-gray-900">{title}</h3>
            <p className="text-xs leading-relaxed text-gray-500">{message}</p>
          </motion.div>

          {/* ── What happens next steps ── */}
          {success && steps?.length > 0 && (
            <motion.div
              className="mb-4 space-y-1.5"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28 }}
            >
              {steps.map(({ icon: Icon, text, accent }, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.33 + i * 0.07 }}
                  className="flex gap-2.5 items-center px-3 py-2 bg-gray-50 rounded-xl border border-gray-100"
                >
                  <span className="flex justify-center items-center w-6 h-6 rounded-lg shrink-0"
                    style={{ background: accent + "18" }}>
                    <Icon size={12} style={{ color: accent }} strokeWidth={2} />
                  </span>
                  <span className="text-xs font-medium text-gray-700">{text}</span>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* ── Details / copyable code ── */}
          {(details || code) ? (
            <motion.div
              className={`mb-4 rounded-2xl border overflow-hidden ${
                success
                  ? "bg-[#FFD221]/[0.07] border-[#FFD221]/30"
                  : "bg-rose-50 border-rose-100"
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
            >
              {/* label row */}
              {details && (
                <p className={`px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-widest ${
                  success ? "text-[#9a7b00]" : "text-rose-500"
                }`}>
                  {details}
                </p>
              )}

              {/* code + copy button */}
              {code && (
                <div className="flex gap-3 justify-between items-center px-4 pt-1 pb-3">
                  <span className={`font-mono text-lg font-black tracking-[0.25em] ${
                    success ? "text-black" : "text-rose-700"
                  }`}>
                    {code}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all shrink-0 ${
                      copied
                        ? success
                          ? "bg-[#FFD221] text-black"
                          : "bg-rose-500 text-white"
                        : success
                          ? "bg-[#FFD221]/20 text-[#9a7b00] hover:bg-[#FFD221]/35"
                          : "bg-rose-100 text-rose-600 hover:bg-rose-200"
                    }`}
                  >
                    {copied ? <Check size={11} strokeWidth={3} /> : <Copy size={11} />}
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
              )}
            </motion.div>
          ) : null}

          {/* ── CTA ── */}
          <motion.button
            type="button"
            onClick={onClose}
            className={`w-full py-3 rounded-2xl text-sm font-bold transition-all ${
              success
                ? "bg-[#FFD221] text-black hover:bg-[#c9a000] shadow-lg shadow-[#FFD221]/30"
                : "bg-rose-500 text-white hover:bg-rose-600 shadow-lg shadow-rose-500/25"
            }`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.01, y: -1 }}
            whileTap={{ scale: 0.98 }}
          >
            {success ? (
              <span className="inline-flex gap-2 justify-center items-center">
                Got it
                <ArrowRight size={14} />
              </span>
            ) : "Dismiss"}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

const LoadingOverlay = ({ open, label }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0 }}
        className="overflow-hidden w-full max-w-md bg-white rounded-3xl border shadow-2xl border-white/20"
      >
        <div className="h-1.5 bg-gradient-to-r from-[#FFD221] via-[#c9a000] to-black" />
        <div className="p-7 text-center sm:p-8">
          <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-[#FFD221]/[0.15] flex items-center justify-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
              className="w-10 h-10 border-4 border-[#FFD221]/30 border-t-[#FFD221] rounded-full"
            />
          </div>
          <p className="text-sm font-bold text-gray-900">{label}</p>
          <p className="mt-2 text-xs text-gray-500">Please do not refresh or close this page.</p>
        </div>
      </motion.div>
    </div>
  );
};

const PayloadPreviewModal = ({ open, payloadText, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(payloadText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center px-4">
      <motion.div
        className="absolute inset-0 bg-[#060f0c]/80 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, y: 18, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.97 }}
        className="relative z-10 flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-[0_32px_80px_rgba(0,0,0,0.35)]"
      >
        <div className="h-1.5 bg-gradient-to-r from-[#FFD221] via-[#c9a000] to-black" />
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-[#9a7b00]">Final JSON Payload</p>
            <p className="mt-1 text-xs leading-relaxed text-gray-500">
              This is the JSON payload that will be posted to the backend. Uploaded files are included as base64 data URLs.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>
        <pre className="min-h-0 flex-1 overflow-auto bg-slate-950 px-4 py-4 text-xs leading-relaxed text-slate-100">
          {payloadText}
        </pre>
        <div className="flex flex-col gap-2 border-t border-gray-100 px-5 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-bold text-gray-700 transition hover:bg-gray-50"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy payload"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-xl bg-[#FFD221] px-4 text-sm font-bold text-black transition hover:bg-[#c9a000]"
          >
            Done
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   STEP CONTENT COMPONENTS
══════════════════════════════════════════════════════════════ */

/** Company logo upload — shown at the top of Step 1. */
const LogoUploader = ({ entry, onUpload, onRemove, error }) => {
  const isUrl     = isUrlEntry(entry);
  const isBase64  = isBase64Entry(entry);
  const hasFile   = isUrl || isBase64;
  const previewSrc = useEntryPreviewUrl(entry);

  return (
    <div className="w-full">
      <Label required>Company Logo</Label>
      <p className="text-[11px] text-gray-500 mb-3 -mt-0.5 leading-relaxed">
        Displayed on your marketplace listing. PNG or SVG with a transparent background is recommended.
      </p>
      <div className={`rounded-2xl border-2 p-4 sm:p-5 transition-all duration-200 ${
        hasFile
          ? "border-[#FFD221]/60 bg-white"
          : error
            ? "border-dashed border-rose-300 bg-rose-50/40"
            : "border-dashed border-[#FFD221]/35 bg-white/80 hover:border-[#FFD221]/55 hover:bg-[#fffdf8]"
      }`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
          {/* Preview */}
          <div className={`mx-auto shrink-0 sm:mx-0 flex h-24 w-24 sm:h-28 sm:w-28 items-center justify-center overflow-hidden rounded-2xl transition-all ${
            hasFile
              ? "bg-white border border-gray-100 shadow-sm"
              : error
                ? "bg-rose-50 border border-rose-200"
                : "bg-[#f7f4ec] border border-[#FFD221]/25"
          }`}>
            {previewSrc ? (
              <img src={previewSrc} alt="Company logo preview" className="h-full w-full object-contain p-2" />
            ) : (
              <Building2 size={32} className={error ? "text-rose-300" : "text-[#c9a000]/60"} />
            )}
          </div>

          <div className="min-w-0 flex-1 text-center sm:text-left">
            {hasFile ? (
              <>
                <p className="text-sm font-bold text-gray-900 break-words">{entry.name}</p>
                <p className={`mt-1 text-xs italic ${isUrl ? "text-[#9a7b00]" : "text-gray-500"}`}>
                  {isUrl ? "Saved on server — use Replace to update" : "Ready to upload with your application"}
                </p>
                <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-start">
                  <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#FFD221]/50 bg-[#FFD221] px-4 py-2.5 text-sm font-bold text-black shadow-sm transition hover:bg-[#c9a000]">
                    <Upload size={16} />
                    Replace file
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.svg,.webp"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={onRemove}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-rose-600 transition hover:bg-rose-50"
                  >
                    <X size={16} />
                    Remove
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-800">Upload your company logo</p>
                <p className="mt-1 text-xs text-gray-500">PNG, JPG, SVG or WEBP · max 5 MB</p>
                <label className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-[#FFD221]/50 bg-[#FFD221] px-4 py-3 text-sm font-bold text-black shadow-sm transition hover:border-[#c9a000] hover:bg-[#c9a000] sm:inline-flex sm:w-auto sm:min-w-[10rem]">
                  <Upload size={18} />
                  Browse files
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.svg,.webp"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
                  />
                </label>
              </>
            )}
          </div>
        </div>
      </div>
      <FieldError msg={error} />
    </div>
  );
};

const Step1 = ({ form, update, errors }) => {
  const [autoSyncTradingName, setAutoSyncTradingName] = useState(false);
  const [citySearch, setCitySearch] = useState(form.city || "");
  const handleLogoUpload = (file) => {
    fileToDocEntry(file)
      .then((entry) => update("logo", entry))
      .catch(() => {});
  };
  const legalName = String(form.legalName || "");
  const tradingName = String(form.tradingName || "");
  const selectedRegion = form.region || "";
  const regionCities = useMemo(() => REGION_CITIES[selectedRegion] || [], [selectedRegion]);
  const filteredCities = useMemo(() => {
    const query = citySearch.trim().toLowerCase();
    if (!query) return regionCities;
    return regionCities.filter((city) => city.toLowerCase().includes(query));
  }, [citySearch, regionCities]);
  const hasExactCityMatch = useMemo(
    () => regionCities.some((city) => city.toLowerCase() === citySearch.trim().toLowerCase()),
    [citySearch, regionCities],
  );
  const legalTrimmed = legalName.trim();
  const namesMatch = legalTrimmed && legalTrimmed === tradingName.trim();
  useEffect(() => {
    setCitySearch(form.city || "");
  }, [form.city]);
  const handleLegalNameChange = (e) => {
    const nextLegalName = e.target.value;
    update("legalName", nextLegalName);
    if (autoSyncTradingName) {
      // Batched with the legal name update for smoother typing.
      update("tradingName", nextLegalName);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-[#FFD221]/35 bg-gradient-to-b from-white to-[#fffdf4] p-5 sm:p-6 shadow-sm">
        <div className="flex flex-col gap-6">
          <div className="max-w-2xl">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#9a7b00]">InfoBoxx partner profile</p>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
              Capture the legal business identity users and sponsors can trust.
            </p>
          </div>
          <div id="step1-logo" className="w-full border-t border-[#FFD221]/20 pt-6">
            <LogoUploader
              entry={form.logo}
              onUpload={handleLogoUpload}
              onRemove={() => update("logo", null)}
              error={errors.logo}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Input id="step1-legal-name" label="Company Name" required placeholder="As registered with the Registrar General"
          value={form.legalName} onChange={handleLegalNameChange} error={errors.legalName} />
        <div id="step1-trading-name">
          <div className="flex flex-wrap gap-2 justify-between items-center">
            <Label>Trading / Brand Name</Label>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => {
                  update("tradingName", legalName);
                  setAutoSyncTradingName(false);
                }}
                disabled={!legalTrimmed || namesMatch}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#FFD221]/40 bg-[#FFD221]/15 px-2.5 py-1 text-[11px] font-bold text-[#9a7b00] transition-all hover:bg-[#FFD221]/25 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
              >
                <Wand2 size={12} />
                {namesMatch ? "Already same" : "Use legal name"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAutoSyncTradingName(false);
                  update("tradingName", "");
                }}
                disabled={!tradingName}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] font-bold text-gray-500 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X size={11} />
                Clear
              </button>
            </div>
          </div>
          <input
            id="step1-trading-name-input"
            name="tradingName"
            autoComplete="off"
            placeholder="If different from legal name"
            value={form.tradingName}
            onChange={(e) => {
              setAutoSyncTradingName(false);
              update("tradingName", e.target.value);
            }}
            className="mt-2 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition-all duration-200 placeholder:text-gray-300 hover:border-gray-300 focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/50"
          />
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <p className={`text-xs ${namesMatch ? "font-semibold text-emerald-700" : "text-gray-400"}`}>
              {autoSyncTradingName
                ? "Trading name updates as legal name changes."
                : namesMatch
                  ? "Trading / Brand Name is synced with Legal Company Name."
                  : "Tap “Use legal name” if both names are the same."}
            </p>
            <button
              type="button"
              onClick={() => setAutoSyncTradingName((prev) => !prev)}
              disabled={!legalTrimmed}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-bold transition-all ${
                autoSyncTradingName
                  ? "border-[#FFD221]/50 bg-[#FFD221]/20 text-black"
                  : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
              } disabled:cursor-not-allowed disabled:opacity-50`}
            >
              <Sparkles size={11} />
              {autoSyncTradingName ? "Auto-sync on" : "Auto-sync"}
            </button>
          </div>
        </div>
      </div>
      <div id="step1-company-type">
      <SelectWithOther
        label="Business Type" required value={form.companyType}
        onChange={(e) => update("companyType", e.target.value)} error={errors.companyType}
        customValue={form.companyTypeCustom}
        onCustomChange={(val) => update("companyTypeCustom", val)}
        customLabel="Describe your company type"
        customPlaceholder="e.g. Fintech, Credit Union…"
        customError={errors.companyTypeCustom}
      >
        <option value="">Select company type…</option>
        {COMPANY_TYPE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
        <option value="Other">Other (specify)</option>
      </SelectWithOther>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Input id="step1-year-incorporated" label="Year Established" required type="number" placeholder="e.g. 2010"
          min="1800" max={new Date().getFullYear()}
          value={form.yearIncorporated} onChange={(e) => update("yearIncorporated", e.target.value)} error={errors.yearIncorporated} />
        <Input label="Country" required
          value={form.countryOfIncorporation} onChange={(e) => update("countryOfIncorporation", e.target.value)} />
      </div>
      <Textarea id="step2-registered-address" label="Company Address" required placeholder="Full legal or operational company address"
        value={form.registeredAddress} onChange={(e) => update("registeredAddress", e.target.value)} error={errors.registeredAddress} />
      <Textarea
        id="step1-operational-address"
        label="Operational Address"
        placeholder="Optional — leave blank if same as company address"
        value={form.operationalAddress}
        onChange={(e) => update("operationalAddress", e.target.value)}
      />
      <Textarea
        id="step1-business-description"
        label="Business Description"
        placeholder="Optional — briefly describe what your organisation does"
        value={form.businessDescription}
        onChange={(e) => update("businessDescription", e.target.value)}
      />
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Select id="step2-region" label="Region" required value={form.region}
          onChange={(e) => {
            const nextRegion = e.target.value;
            update("region", nextRegion);
            update("city", "");
            setCitySearch("");
          }} error={errors.region}>
          <option value="">Select region…</option>
          {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
        <Input label="GPS Address" placeholder="Optional"
          value={form.gpsLocation} onChange={(e) => update("gpsLocation", e.target.value)} />
      </div>
      <div id="step2-city">
        <Label required>City</Label>
        {!selectedRegion ? (
          <p className="mt-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500">
            Select a region first to see and search available cities.
          </p>
        ) : (
          <div className="space-y-3">
            <input
              value={citySearch}
              onChange={(e) => {
                setCitySearch(e.target.value);
                update("city", e.target.value);
              }}
              placeholder={`Search cities in ${selectedRegion}...`}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/40"
            />
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {filteredCities.map((city) => (
                <button
                  key={city}
                  type="button"
                  onClick={() => {
                    update("city", city);
                    setCitySearch(city);
                  }}
                  className={`rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                    form.city === city
                      ? "border-[#FFD221] bg-[#FFD221]/20 text-black"
                      : "border-gray-200 bg-white text-gray-600 hover:border-[#FFD221]/50 hover:bg-[#FFD221]/10"
                  }`}
                >
                  {city}
                </button>
              ))}
            </div>
            {citySearch.trim() && !hasExactCityMatch && (
              <button
                type="button"
                onClick={() => update("city", citySearch.trim())}
                className="inline-flex items-center gap-1.5 rounded-xl border border-[#FFD221]/50 bg-white px-3 py-2 text-xs font-bold text-[#9a7b00] transition hover:bg-[#FFD221]/10"
              >
                <Plus size={13} />
                Use "{citySearch.trim()}" as city
              </button>
            )}
          </div>
        )}
        <FieldError msg={errors.city} />
      </div>
      <Input label="Website URL" type="url" placeholder="https://yourcompany.com (optional)"
        value={form.websiteUrl} onChange={(e) => update("websiteUrl", e.target.value)} error={errors.websiteUrl} />
    </div>
  );
};

const Step2 = ({ form, update, errors, phoneCheck = { status: "idle" }, emailCheck = { status: "idle" }, onPhoneChange, onEmailChange }) => {
  const contact = form.primaryContact || {};
  const secondaryContacts = Array.isArray(form.secondaryContacts) ? form.secondaryContacts : [];
  const updateContact = (field, val) => update("primaryContact", { ...contact, [field]: val });

  const addSecondary = () => {
    if (secondaryContacts.length >= 3) return;
    update("secondaryContacts", [...secondaryContacts, { ...EMPTY_SECONDARY_CONTACT }]);
  };

  const updateSecondary = (index, field, val) => {
    const next = secondaryContacts.map((item, i) => (i === index ? { ...item, [field]: val } : item));
    update("secondaryContacts", next);
  };

  const removeSecondary = (index) => {
    update(
      "secondaryContacts",
      secondaryContacts.filter((_, i) => i !== index),
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 p-4 rounded-xl border border-[#FFD221]/30 bg-[#FFD221]/[0.06]">
        <KeyRound size={14} className="mt-0.5 shrink-0 text-[#9a7b00]" />
        <p className="text-xs leading-relaxed text-gray-700">
          We will use this representative for verification updates, lead routing, and draft recovery.
          You may optionally add up to three secondary contacts with the same details.
        </p>
      </div>

      <div id="step2-primary-contact" className="space-y-4">
        <p className="text-xs font-black uppercase tracking-wider text-[#9a7b00]">Primary contact</p>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Input id="step2-primary-full-name" label="Full Name" required placeholder="Primary representative"
            value={contact.fullName} onChange={(e) => updateContact("fullName", e.target.value)} error={errors.primaryContactFullName} />
          <Input id="step2-primary-role" label="Position / Role" required placeholder="e.g. Managing Partner"
            value={contact.jobTitle} onChange={(e) => updateContact("jobTitle", e.target.value)} error={errors.primaryContactJobTitle} />
          <CheckedInput
            id="step2-primary-phone"
            label="Phone Number"
            required
            type="tel"
            placeholder="233 XX XXX XXXX"
            value={form.corporatePhone}
            onChange={(e) => {
              const nextPhone = e.target.value.replace(/^\+/, "");
              updateContact("phone", nextPhone);
              onPhoneChange ? onPhoneChange(nextPhone) : update("corporatePhone", nextPhone);
            }}
            error={errors.corporatePhone || errors.primaryContactPhone}
            checkStatus={phoneCheck.status}
            takenMsg="This phone number is already registered"
          />
          <CheckedInput
            id="step2-primary-email"
            label="Email Address"
            required
            type="email"
            placeholder="name@company.com"
            value={form.corporateEmail}
            onChange={(e) => {
              updateContact("email", e.target.value);
              onEmailChange ? onEmailChange(e.target.value) : update("corporateEmail", e.target.value);
            }}
            error={errors.corporateEmail || errors.primaryContactEmail}
            checkStatus={emailCheck.status}
            takenMsg="This email address is already registered"
          />
        </div>
        <Input label="Alternative Phone Number" type="tel" placeholder="Optional"
          value={contact.alternatePhone || ""} onChange={(e) => updateContact("alternatePhone", e.target.value.replace(/^\+/, ""))} />
      </div>

      <div id="step2-secondary-contacts" className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-gray-700">Secondary contacts</p>
            <p className="mt-1 text-xs leading-relaxed text-gray-500">
              Optional — add backup contacts for verification and lead routing ({secondaryContacts.length} of 3).
            </p>
          </div>
          {secondaryContacts.length < 3 && (
            <button
              type="button"
              onClick={addSecondary}
              className="inline-flex items-center gap-1.5 rounded-xl border border-[#FFD221]/50 bg-[#FFD221]/15 px-3 py-2 text-xs font-bold text-[#9a7b00] transition hover:bg-[#FFD221]/25"
            >
              <Plus size={14} />
              {secondaryContacts.length === 0 ? "Add secondary contact" : "Add another"}
            </button>
          )}
        </div>

        {secondaryContacts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/80 px-4 py-5 text-center">
            <p className="text-sm text-gray-500">No secondary contacts added.</p>
            <button
              type="button"
              onClick={addSecondary}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#9a7b00] hover:underline"
            >
              <Plus size={13} />
              Add optional secondary contact
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {secondaryContacts.map((sc, index) => (
              <ContactBlock
                key={`secondary-${index}`}
                title={`Secondary contact ${index + 1}`}
                contact={sc || EMPTY_SECONDARY_CONTACT}
                onChange={(field, val) => updateSecondary(index, field, val)}
                onRemove={() => removeSecondary(index)}
                removable
                fieldErrors={{
                  fullName: errors?.[`secondaryContact${index}FullName`],
                  jobTitle: errors?.[`secondaryContact${index}JobTitle`],
                  email: errors?.[`secondaryContact${index}Email`],
                  phone: errors?.[`secondaryContact${index}Phone`],
                  idType: errors?.[`secondaryContact${index}IdType`],
                  idNumber: errors?.[`secondaryContact${index}IdNumber`],
                }}
                idErrors={{
                  idFront: errors?.[`secondaryContact${index}IdFront`],
                  idBack: errors?.[`secondaryContact${index}IdBack`],
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Step3 = ({ form, update, errors, editableDocIds = null, flaggedReason = "", docStatuses = {} }) => {
  const contact = form.primaryContact || {};
  const updateContact = (field, val) => update("primaryContact", { ...contact, [field]: val });
  const isPassport = String(contact.idType || "").toLowerCase().includes("passport");
  const handleIdUpload = (side, file) => {
    fileToDocEntry(file)
      .then((entry) => update("primaryContact", (prev) => ({ ...prev, [side]: entry })))
      .catch(() => {});
  };

  useEffect(() => {
    if (isPassport && contact.idBack) updateContact("idBack", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPassport, contact.idBack]);

  return (
    <div className="space-y-6">
      <div id="step3-business-identifiers" className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Input id="step3-registration-number" label="Business Registration Number" required placeholder="e.g. CS-12345"
          value={form.registrationNumber} onChange={(e) => update("registrationNumber", e.target.value)} error={errors.registrationNumber} />
        <Input id="step3-tin" label="Tax Identification Number (TIN)" required placeholder="e.g. C0000000000"
          value={form.tin} onChange={(e) => update("tin", e.target.value)} error={errors.tin} />
      </div>

      <div id="step3-regulatory" className="rounded-2xl border border-gray-200 bg-white p-5 space-y-5">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-[#9a7b00]">Regulatory details</p>
          <p className="mt-1 text-sm leading-relaxed text-gray-500">
            Optional — complete if your organisation is licensed or supervised by a Ghanaian regulator.
          </p>
        </div>
        <SelectWithOther
          label="Primary Regulator"
          value={form.primaryRegulator}
          onChange={(e) => update("primaryRegulator", e.target.value)}
          customValue={form.primaryRegulatorCustom}
          onCustomChange={(val) => update("primaryRegulatorCustom", val)}
          customLabel="Specify regulator"
          customPlaceholder="e.g. FDA, NCA…"
        >
          <option value="">Select regulator (optional)…</option>
          {REGULATOR_OPTIONS.filter((r) => r !== "Other").map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
          <option value="Other">Other (specify)</option>
        </SelectWithOther>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Input
            label="License Number"
            placeholder="Optional"
            value={form.licenseNumber}
            onChange={(e) => update("licenseNumber", e.target.value)}
          />
          <Select
            label="License Class"
            value={form.licenseClass || ""}
            onChange={(e) => update("licenseClass", e.target.value)}
          >
            <option value="">Select class (optional)…</option>
            {LICENSE_CLASS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </Select>
          <Input
            label="License Issue Date"
            type="date"
            value={form.licenseIssueDate}
            onChange={(e) => update("licenseIssueDate", e.target.value)}
          />
          <Input
            label="License Expiry Date"
            type="date"
            value={form.licenseExpiryDate}
            onChange={(e) => update("licenseExpiryDate", e.target.value)}
          />
        </div>
        <CheckboxField
          label="I confirm this organisation is in good standing with the named regulator and meets applicable compliance obligations."
          checked={Boolean(form.complianceAccepted)}
          onChange={() => update("complianceAccepted", !form.complianceAccepted)}
        />
      </div>

      <Step6
        form={form}
        update={update}
        errors={errors}
        editableDocIds={editableDocIds}
        flaggedReason={flaggedReason}
        docStatuses={docStatuses}
        visibleDocIds={["cert_incorporation", "cert_commence"]}
      />
      <div id="step3-identity" className="rounded-2xl border border-gray-200 bg-white p-5">
        <p className="text-xs font-black uppercase tracking-wider text-[#9a7b00]">Identity Verification (KYC)</p>
        <p className="mt-1 text-sm leading-relaxed text-gray-500">
          Verify the responsible individual attached to this provider application.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <SingleSelectCards
            id="step3-id-type"
            label="ID Type"
            required
            options={ID_TYPE_OPTIONS}
            selected={contact.idType}
            onSelect={(value) => updateContact("idType", value)}
            error={errors.primaryContactIdType}
            optionsClassName="grid grid-cols-1 gap-2"
          />
          <Input id="step3-id-number" label="ID Number" required placeholder="Enter ID number"
            value={contact.idNumber || ""} onChange={(e) => updateContact("idNumber", e.target.value)} error={errors.primaryContactIdNumber} />
        </div>
        <div className="mt-5">
          <Label required>Upload ID Document</Label>
          <p className="mb-2.5 -mt-0.5 text-[11px] leading-relaxed text-gray-400">
            Upload the front and back of the selected ID. Passport only requires the photo page.
          </p>
          <div className={`grid grid-cols-1 gap-3 ${isPassport ? "max-w-md" : "sm:grid-cols-2"}`}>
            <div>
              <IdCardUploader
                label={isPassport ? "Passport Photo Page" : "Front Side"}
                hint="JPG or PNG · max 5 MB"
                fieldKey="primary_contact_id_front"
                entry={contact.idFront}
                onUpload={(file) => handleIdUpload("idFront", file)}
                onRemove={() => updateContact("idFront", null)}
              />
              <FieldError msg={errors.primaryContactIdFront} />
            </div>
            {!isPassport && (
              <div>
                <IdCardUploader
                  label="Back Side"
                  hint="JPG or PNG · max 5 MB"
                  fieldKey="primary_contact_id_back"
                  entry={contact.idBack}
                  onUpload={(file) => handleIdUpload("idBack", file)}
                  onRemove={() => updateContact("idBack", null)}
                />
                <FieldError msg={errors.primaryContactIdBack} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const buildSpecificServiceOptions = (category, subcategory) => {
  const base = subcategory || category || "Service";
  if (!category && !subcategory) return SPECIFIC_SERVICE_OPTIONS;
  return [
    `${base} Consultation`,
    `${base} Advisory`,
    `${base} Documentation`,
    `${base} Application Support`,
    `${base} Client Support`,
    "Content Sponsorship",
  ];
};

const Step4 = ({ form, update, errors }) => {
  const { industries, menuByIndustry } = useCategories();
  const [apiCategoryMap, setApiCategoryMap] = useState({});
  const [apiCategoryOptions, setApiCategoryOptions] = useState([]);
  const [categoriesHydrated, setCategoriesHydrated] = useState(false);
  const selectedCategory = form.primaryCategories?.[0] || "";
  const [subcategorySearch, setSubcategorySearch] = useState("");
  const selectedSubcategory = form.subCategories?.[0] || "";

  useEffect(() => {
    if (!industries.length) {
      setCategoriesHydrated(true);
      return;
    }
    const map = {};
    industries.forEach((industry) => {
      map[industry.label] = (menuByIndustry[industry.id] || [])
        .map((item) => item.label)
        .filter(Boolean);
    });
    setApiCategoryMap(map);
    setApiCategoryOptions(industries.map((industry) => industry.label).filter(Boolean));
    setCategoriesHydrated(true);
  }, [industries, menuByIndustry]);

  const categoryOptions = useMemo(() => {
    const base = apiCategoryOptions.length ? apiCategoryOptions : SERVICE_CATEGORY_OPTIONS;
    if (!selectedCategory || base.includes(selectedCategory)) return base;
    return [selectedCategory, ...base];
  }, [apiCategoryOptions, selectedCategory]);

  const availableSubs = useMemo(() => {
    const apiSubs = apiCategoryMap[selectedCategory];
    if (Array.isArray(apiSubs)) return apiSubs;
    const fallbackIndustryId = SERVICE_CATEGORY_TO_INDUSTRY_ID[selectedCategory];
    if (!fallbackIndustryId) return [];
    return getItemsByIndustry(fallbackIndustryId).map((item) => item.name).filter(Boolean);
  }, [apiCategoryMap, selectedCategory]);
  const filteredSubs = useMemo(() => {
    const query = subcategorySearch.trim().toLowerCase();
    if (!query) return availableSubs;
    return availableSubs.filter((item) => item.toLowerCase().includes(query));
  }, [availableSubs, subcategorySearch]);
  const specificServiceOptions = useMemo(
    () => buildSpecificServiceOptions(selectedCategory, selectedSubcategory),
    [selectedCategory, selectedSubcategory],
  );
  const selectedCoverageAreas = Array.isArray(form.coverageAreas) ? form.coverageAreas : [];
  const allRegionsSelected = REGIONS.every((region) => selectedCoverageAreas.includes(region));
  const customSpecificServices = (form.specificServices || []).filter((value) => !specificServiceOptions.includes(value));
  const customLanguages = (form.languagesSpoken || []).filter((value) => !LANGUAGE_OPTIONS.includes(value));

  useEffect(() => {
    if (!categoriesHydrated || !selectedSubcategory || !availableSubs.length) return;
    const canonical = availableSubs.find((sub) => sub.toLowerCase() === selectedSubcategory.toLowerCase());
    if (canonical && canonical !== selectedSubcategory) {
      update("subCategories", [canonical]);
    }
  }, [availableSubs, categoriesHydrated, selectedSubcategory, update]);

  const toggleMulti = (field, val) => {
    const curr = Array.isArray(form[field]) ? form[field] : [];
    update(field, curr.includes(val) ? curr.filter((v) => v !== val) : [...curr, val]);
  };

  const addUniqueValue = (field, val) => {
    const trimmed = String(val || "").trim();
    if (!trimmed) return;
    const curr = Array.isArray(form[field]) ? form[field] : [];
    if (!curr.some((item) => item.toLowerCase() === trimmed.toLowerCase())) {
      update(field, [...curr, trimmed]);
    }
  };

  const removeValue = (field, val) => {
    const curr = Array.isArray(form[field]) ? form[field] : [];
    update(field, curr.filter((item) => item !== val));
  };

  return (
    <div className="space-y-8">
      <div id="step4-primary-categories">
        <Select label="Primary Category" required value={selectedCategory}
          onChange={(e) => {
            update("primaryCategories", e.target.value ? [e.target.value] : []);
            update("subCategories", []);
            setSubcategorySearch("");
          }}
          error={errors.primaryCategories}>
          <option value="">Select category…</option>
          {categoryOptions.map((category) => (
            <option key={category} value={category}>{category}</option>
          ))}
        </Select>
      </div>

      {availableSubs.length > 0 && (
        <div id="step4-sub-categories">
          <Label required>Subcategory</Label>
          <input
            value={subcategorySearch}
            onChange={(e) => setSubcategorySearch(e.target.value)}
            placeholder="Search subcategories..."
            className="mb-3 mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#FFD221] focus:ring-2 focus:ring-[#FFD221]/40"
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {filteredSubs.map((sub) => (
              <button
                key={sub}
                type="button"
                onClick={() => update("subCategories", [sub])}
                className={`rounded-xl border px-3 py-2.5 text-left text-xs font-semibold transition ${
                  selectedSubcategory === sub
                    ? "border-[#FFD221] bg-[#FFD221]/20 text-black shadow-sm"
                    : "border-gray-200 bg-white text-gray-600 hover:border-[#FFD221]/60 hover:bg-[#FFD221]/10"
                }`}
              >
                {sub}
              </button>
            ))}
          </div>
          {filteredSubs.length === 0 && (
            <p className="mt-2 text-xs text-gray-400">No subcategories match your search.</p>
          )}
          <FieldError msg={errors.subCategories} />
        </div>
      )}

      <div id="step4-specific-services">
        <MultiSelect label="Specific Services Offered" required options={specificServiceOptions}
          selected={form.specificServices || []} onToggle={(v) => toggleMulti("specificServices", v)} error={errors.specificServices}
          optionsContainerClass="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
          optionClassName="w-full justify-center" />
        <AddOtherChips
          label="Add other service"
          placeholder="e.g. Contract review, onboarding support"
          values={customSpecificServices}
          onAdd={(value) => addUniqueValue("specificServices", value)}
          onRemove={(value) => removeValue("specificServices", value)}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Input id="step4-years-experience" label="Years of Experience" required type="number" min="0" placeholder="e.g. 8"
          value={form.yearsOfExperience} onChange={(e) => update("yearsOfExperience", e.target.value)} error={errors.yearsOfExperience} />
        <Input label="Number of Staff" type="number" min="0" placeholder="Optional"
          value={form.numberOfStaff} onChange={(e) => update("numberOfStaff", e.target.value)} />
      </div>

      <div id="step4-coverage-areas">
        <MultiSelect label="Regions Served" required options={REGIONS}
          selected={selectedCoverageAreas} onToggle={(v) => toggleMulti("coverageAreas", v)} error={errors.coverageAreas}
          optionsContainerClass="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2"
          optionClassName="w-full justify-center"
          headerAction={(
            <button
              type="button"
              onClick={() => update("coverageAreas", allRegionsSelected ? [] : REGIONS)}
              className="rounded-lg border border-[#FFD221]/40 bg-[#FFD221]/[0.10] px-3 py-1.5 text-[11px] font-bold text-[#9a7b00] transition hover:bg-[#FFD221]/20"
            >
              {allRegionsSelected ? "Clear regions" : "Nationwide (All regions)"}
            </button>
          )} />
      </div>

      <SingleSelectCards
        id="step4-service-mode"
        label="Service Mode"
        required
        options={SERVICE_MODES}
        selected={form.serviceMode}
        onSelect={(value) => update("serviceMode", value)}
        error={errors.serviceMode}
      />

      <div id="step4-languages">
        <MultiSelect label="Languages Spoken" options={LANGUAGE_OPTIONS}
          selected={form.languagesSpoken || []} onToggle={(v) => toggleMulti("languagesSpoken", v)}
          optionsContainerClass="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2"
          optionClassName="w-full justify-center" />
        <AddOtherChips
          label="Add other language"
          placeholder="e.g. Fante, Nzema"
          values={customLanguages}
          onAdd={(value) => addUniqueValue("languagesSpoken", value)}
          onRemove={(value) => removeValue("languagesSpoken", value)}
        />
      </div>

      <div id="step4-customer-segments">
        <MultiSelect
          label="Customer Segments"
          options={CUSTOMER_SEGMENT_OPTIONS}
          selected={form.customerSegments || []}
          onToggle={(v) => toggleMulti("customerSegments", v)}
          optionsContainerClass="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2"
          optionClassName="w-full justify-center"
        />
      </div>

      <SingleSelectCards
        id="step4-digital-capability"
        label="Digital Capability"
        options={DIGITAL_CAPABILITY_OPTIONS}
        selected={form.digitalCapability}
        onSelect={(value) => update("digitalCapability", value)}
        optionsClassName="grid grid-cols-1 sm:grid-cols-3 gap-3"
      />

      <div id="step4-content" className="rounded-3xl border border-black/[0.08] bg-white p-5 space-y-5">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-gray-700">Content contribution</p>
          <p className="mt-1 text-sm text-gray-500">
            Optional — tell us if you plan to publish educational content on InfoBoxx.
          </p>
        </div>
        <SingleSelectCards
          id="step4-will-provide-content"
          label="Will you provide content?"
          options={YES_NO_OPTIONS}
          selected={form.willProvideContent}
          onSelect={(value) => {
            update("willProvideContent", value);
            if (value === "No") {
              update("contentTypes", []);
              update("hasExistingContent", "");
              update("contentUploadCapability", "");
            }
          }}
          optionsClassName="grid grid-cols-2 gap-3"
        />
        {form.willProvideContent === "Yes" && (
          <>
            <MultiSelect
              label="Content Types"
              options={CONTENT_TYPE_OPTIONS}
              selected={form.contentTypes || []}
              onToggle={(v) => toggleMulti("contentTypes", v)}
              optionsContainerClass="grid grid-cols-2 sm:grid-cols-3 gap-2"
              optionClassName="w-full justify-center"
            />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <SingleSelectCards
                id="step4-has-existing-content"
                label="Do you already have content?"
                options={YES_NO_OPTIONS}
                selected={form.hasExistingContent}
                onSelect={(value) => update("hasExistingContent", value)}
                optionsClassName="grid grid-cols-2 gap-3"
              />
              <SingleSelectCards
                id="step4-content-upload-capability"
                label="Can you upload content yourself?"
                options={YES_NO_OPTIONS}
                selected={form.contentUploadCapability}
                onSelect={(value) => update("contentUploadCapability", value)}
                optionsClassName="grid grid-cols-2 gap-3"
              />
            </div>
          </>
        )}
      </div>

      <div className="rounded-3xl border border-black/[0.08] bg-white p-5">
        <p className="text-xs font-black uppercase tracking-wider text-gray-700">Lead management preferences</p>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <SingleSelectCards
            id="step4-preferred-contact"
            label="Preferred Contact Method"
            required
            options={CONTACT_METHODS}
            selected={form.preferredContactMethod}
            onSelect={(value) => update("preferredContactMethod", value)}
            error={errors.preferredContactMethod}
            optionsClassName="grid grid-cols-1 gap-3"
          />
          <SingleSelectCards
            id="step4-response-time-sla"
            label="Response Time SLA"
            required
            options={RESPONSE_TIME_OPTIONS}
            selected={form.responseTimeSla}
            onSelect={(value) => update("responseTimeSla", value)}
            error={errors.responseTimeSla}
            optionsClassName="grid grid-cols-1 gap-3"
          />
          <SingleSelectCards
            id="step4-instant-leads"
            label="Do you accept instant leads?"
            required
            options={YES_NO_OPTIONS}
            selected={form.acceptsInstantLeads}
            onSelect={(value) => update("acceptsInstantLeads", value)}
            error={errors.acceptsInstantLeads}
            optionsClassName="grid grid-cols-1 gap-3"
          />
          <SingleSelectCards
            id="step4-appointment-availability"
            label="Appointment Booking Enabled"
            required
            options={YES_NO_OPTIONS}
            selected={form.appointmentAvailability}
            onSelect={(value) => update("appointmentAvailability", value)}
            error={errors.appointmentAvailability}
            optionsClassName="grid grid-cols-1 gap-3"
          />
        </div>
      </div>
    </div>
  );
};

const ContactBlock = ({
  contact, onChange, onRemove, title, removable = false, optional = false, idErrors = {}, fieldErrors = {},
}) => {
  const fieldKey = title.replace(/\s+/g, "_").toLowerCase();
  const isPassport = String(contact?.idType || "").toLowerCase().includes("passport");

  const handleIdUpload = (side, file) => {
    fileToDocEntry(file)
      .then((entry) => onChange(side, entry))
      .catch(() => {});
  };

  useEffect(() => {
    if (isPassport && contact?.idBack) onChange("idBack", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPassport, contact?.idBack]);

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200">
      <div className={`flex items-center justify-between px-5 py-3 ${removable ? "bg-gray-50" : "bg-[#FFD221]/[0.05]"} border-b border-gray-200`}>
        <p className="text-xs font-bold tracking-wider text-gray-600 uppercase">{title}</p>
        {removable && (
          <button type="button" onClick={onRemove}
            className="p-1.5 rounded-lg text-gray-400 hover:text-rose-500 hover:bg-rose-50 transition-colors">
            <Trash2 size={13} />
          </button>
        )}
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Full Name" required={!optional} placeholder="Full name"
            value={contact.fullName || ""} onChange={(e) => onChange("fullName", e.target.value)}
            error={fieldErrors.fullName} />
          <Input label="Job Title / Role" required={!optional} placeholder="e.g. Head of Digital"
            value={contact.jobTitle || ""} onChange={(e) => onChange("jobTitle", e.target.value)}
            error={fieldErrors.jobTitle} />
          <Input label="Email Address" required={!optional} type="email" placeholder="name@company.com"
            value={contact.email || ""} onChange={(e) => onChange("email", e.target.value)}
            error={fieldErrors.email} />
          <Input label="Phone Number" required={!optional} type="tel" placeholder="233 XX XXX XXXX"
            value={contact.phone || ""} onChange={(e) => onChange("phone", e.target.value.replace(/^\+/, ""))}
            error={fieldErrors.phone} />
          <Input label="Alternative Phone Number" type="tel" placeholder="Optional"
            value={contact.alternatePhone || ""}
            onChange={(e) => onChange("alternatePhone", e.target.value.replace(/^\+/, ""))} />
          <Input label="ID Number" required={!optional} placeholder="Enter ID number"
            value={contact.idNumber || ""} onChange={(e) => onChange("idNumber", e.target.value)}
            error={fieldErrors.idNumber} />
        </div>

        <SingleSelectCards
          label="ID Type"
          required={!optional}
          options={ID_TYPE_OPTIONS}
          selected={contact.idType || ""}
          onSelect={(value) => onChange("idType", value)}
          error={fieldErrors.idType}
          optionsClassName="grid grid-cols-1 gap-2"
        />

        <div>
          <Label required={!optional}>ID Card Images</Label>
          <p className="text-[11px] text-gray-400 mb-2.5 -mt-0.5 leading-relaxed">
            Upload clear photos or scans of the selected ID.
            {isPassport ? " Passport only needs the photo page." : " Include both front and back."}
          </p>
          <div className={`grid grid-cols-1 gap-3 ${isPassport ? "max-w-md" : "sm:grid-cols-2"}`}>
            <div>
              <IdCardUploader
                label={isPassport ? "Passport photo page" : "Front Side"}
                hint="JPG or PNG · max 5 MB"
                fieldKey={`${fieldKey}_front`}
                entry={contact.idFront}
                onUpload={(file) => handleIdUpload("idFront", file)}
                onRemove={() => onChange("idFront", null)}
              />
              <FieldError msg={idErrors.idFront} />
            </div>
            {!isPassport && (
              <div>
                <IdCardUploader
                  label="Back Side"
                  hint="JPG or PNG · max 5 MB"
                  fieldKey={`${fieldKey}_back`}
                  entry={contact.idBack}
                  onUpload={(file) => handleIdUpload("idBack", file)}
                  onRemove={() => onChange("idBack", null)}
                />
                <FieldError msg={idErrors.idBack} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Kept for backwards-compatible draft rendering experiments; the active flow uses Step2 for contact details.
// eslint-disable-next-line no-unused-vars
const Step5 = ({ form, update, errors }) => {
  const updatePrimary = (field, val) => update("primaryContact", { ...form.primaryContact, [field]: val });
  const addSecondary = () =>
    update("secondaryContacts", [
      ...form.secondaryContacts,
      { ...EMPTY_SECONDARY_CONTACT },
    ]);
  const updateSecondary = (i, field, val) => {
    const updated = [...form.secondaryContacts];
    updated[i] = { ...updated[i], [field]: val };
    update("secondaryContacts", updated);
  };
  const removeSecondary = (i) =>
    update("secondaryContacts", form.secondaryContacts.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      <div id="step5-primary-contact">
        <ContactBlock
          title="Primary Contact"
          contact={form.primaryContact}
          onChange={updatePrimary}
          fieldErrors={{
            fullName: errors?.primaryContactFullName,
            email: errors?.primaryContactEmail,
            phone: errors?.primaryContactPhone,
            idType: errors?.primaryContactIdType,
          }}
          idErrors={{
            idFront: errors?.primaryContactIdFront,
            idBack:  errors?.primaryContactIdBack,
          }}
        />
      </div>

      <div id="step5-secondary-section" className="space-y-5">
        <div className="rounded-2xl border-2 border-[#FFD221]/40 bg-gradient-to-br from-[#FFD221]/[0.08] to-[#FFD221]/[0.02] px-4 py-4 sm:px-5 sm:py-5">
          <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFD221]/20">
              <Users size={18} className="text-[#9a7b00]" />
            </div>
            <div className="min-w-0 flex-1 space-y-1.5">
              <p className="text-xs font-black uppercase tracking-wide text-[#9a7b00]">Required for this application</p>
              <p className="text-sm font-semibold leading-snug text-gray-900">
                Add at least one secondary representative
              </p>
              <p className="text-xs leading-relaxed text-gray-600">
                Beyond your primary contact, we need a second person on file (e.g. compliance, operations, or a backup
                signatory). Complete every field and upload both sides of their ID. You may add up to three secondary
                contacts.
              </p>
            </div>
          </div>
        </div>

        <FieldError msg={errors?.secondaryContactsRequired} />

        {form.secondaryContacts.map((sc, i) => (
          <div key={i} id={`step5-secondary-${i}`}>
            <ContactBlock
              title={`Secondary contact ${i + 1}`}
              contact={sc}
              onChange={(field, val) => updateSecondary(i, field, val)}
              onRemove={form.secondaryContacts.length > 1 ? () => removeSecondary(i) : undefined}
              removable={form.secondaryContacts.length > 1}
              optional={false}
              fieldErrors={{
                fullName: errors?.[`secondaryContact${i}FullName`],
                email: errors?.[`secondaryContact${i}Email`],
                phone: errors?.[`secondaryContact${i}Phone`],
                idType: errors?.[`secondaryContact${i}IdType`],
              }}
              idErrors={{
                idFront: errors?.[`secondaryContact${i}IdFront`],
                idBack:  errors?.[`secondaryContact${i}IdBack`],
              }}
            />
          </div>
        ))}

        {form.secondaryContacts.length < 3 && (
          <button type="button" onClick={addSecondary}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#FFD221]/40 bg-white py-3.5 text-sm font-semibold text-[#9a7b00] shadow-sm transition-all duration-200 hover:border-[#FFD221]/60 hover:bg-[#FFD221]/[0.05]">
            <Plus size={15} />
            {form.secondaryContacts.length === 0
              ? "Add secondary contact (required)"
              : `Add another secondary contact (${form.secondaryContacts.length} of 3)`}
          </button>
        )}
      </div>
    </div>
  );
};

const DocUploader = ({ doc, file, onUpload, onRemove, isLocked = false, flagDocStatus = null }) => {
  const inputId = `doc-${doc.id}`;
  const isUrl    = isUrlEntry(file);
  const isBase64 = isBase64Entry(file);
  const hasFile  = isUrl || isBase64;
  const isFlaggedDoc = flagDocStatus !== null;
  const hasFreshUpload = isFlaggedDoc && !isLocked && isBase64;

  const subLabel = isUrl
    ? "Saved on server — click Replace to change"
    : isBase64
      ? file.name
      : doc.hint;

  /* Derive border/bg for flagged mode */
  const cardClass = isFlaggedDoc
    ? isLocked
      ? "border-emerald-200 bg-emerald-50/40"
      : hasFreshUpload
        ? "border-emerald-300/80 bg-emerald-50/50 ring-1 ring-emerald-200/70"
        : hasFile
          ? "border-amber-300/70 bg-amber-50/40 ring-1 ring-amber-200/60"
          : "border-dashed border-amber-300 bg-amber-50/30 hover:bg-amber-50/60"
    : hasFile
      ? isUrl
        ? "border-[#FFD221]/40 bg-[#FFD221]/[0.05]"
        : "border-[#FFD221]/50 bg-[#FFD221]/[0.07]"
      : "border-dashed border-gray-300 hover:border-[#FFD221]/50 hover:bg-gray-50/70";

  return (
    <motion.div layout
      className={`flex relative flex-col gap-3 p-4 rounded-2xl border transition-all duration-200 group ${cardClass}`}
    >
      {/* Flagged mode status badge strip */}
      {isFlaggedDoc && (
        <div className={`flex items-center gap-2 pb-2.5 border-b ${isLocked ? "border-emerald-100" : "border-amber-100"}`}>
          {isLocked ? (
            <>
              <span className="flex justify-center items-center w-5 h-5 bg-emerald-100 rounded-full shrink-0">
                <Check size={11} className="text-emerald-600" strokeWidth={3} />
              </span>
              <p className="text-[11px] font-bold text-emerald-700">Passed — this document was approved. Replace only if re-review requested.</p>
              <Lock size={11} className="ml-auto text-emerald-400 shrink-0" />
            </>
          ) : hasFreshUpload ? (
            <>
              <span className="flex justify-center items-center w-5 h-5 bg-emerald-100 rounded-full shrink-0">
                <Check size={11} className="text-emerald-600" strokeWidth={3} />
              </span>
              <p className="text-[11px] font-bold text-emerald-700">Updated — new file uploaded. Ready for re-review.</p>
              <span className="ml-auto inline-flex items-center rounded-full border border-emerald-200 bg-emerald-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-emerald-700">
                Ready
              </span>
            </>
          ) : (
            <>
              <span className="flex justify-center items-center w-5 h-5 bg-amber-100 rounded-full shrink-0">
                <AlertTriangle size={11} className="text-amber-600" />
              </span>
              <p className="text-[11px] font-bold text-amber-800">
                {flagDocStatus === "fail" || flagDocStatus === "failed"
                  ? "Failed — please upload a corrected version."
                  : "Pending verification — upload or replace below."}
              </p>
            </>
          )}
        </div>
      )}

      <div className="flex gap-4 justify-between items-center">
        <div className="flex items-center gap-3.5 flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${
            isFlaggedDoc && isLocked
              ? "bg-emerald-100"
              : isFlaggedDoc && !isLocked
                ? "bg-amber-100"
                : hasFile
                  ? "bg-[#FFD221]/20"
                  : "bg-gray-100 group-hover:bg-[#FFD221]/15"
          }`}>
            {isFlaggedDoc && isLocked
              ? <Lock size={16} className="text-emerald-600" />
              : isFlaggedDoc && !isLocked && !hasFile
                ? <CloudUpload size={18} className="text-amber-500" />
                : hasFile
                  ? <CheckCircle2 size={18} className="text-[#9a7b00]" />
                  : <CloudUpload size={18} className="text-gray-400 group-hover:text-[#9a7b00]" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">{doc.label}</p>
            <p className={`text-xs mt-0.5 truncate ${isUrl ? "text-[#9a7b00] italic" : "text-gray-400"}`}>
              {subLabel}
            </p>
            {hasFreshUpload && (
              <p className="mt-1 text-[11px] font-bold text-emerald-700">Replacement captured in this session.</p>
            )}
          </div>
        </div>

        <div className="flex gap-2 items-center shrink-0">
          {/* Replace / Browse */}
          <label htmlFor={inputId}
            className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${
              isLocked
                ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                : isFlaggedDoc && !hasFile
                  ? "cursor-pointer border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                  : "cursor-pointer border-gray-200 bg-white text-gray-600 hover:border-[#FFD221] hover:text-[#9a7b00] hover:bg-[#FFD221]/10"
            }`}>
            {isLocked ? "Locked" : hasFile ? "Replace" : "Upload"}
            <input id={inputId} type="file" accept={doc.accept || ".pdf,.jpg,.jpeg,.png"} className="hidden"
              disabled={isLocked}
              onChange={(e) => e.target.files?.[0] && onUpload(doc.id, e.target.files[0])} />
          </label>
          {/* Remove */}
          {hasFile && !isLocked && (
            <button type="button" onClick={() => onRemove(doc.id)}
              className="p-1.5 text-gray-400 rounded-xl transition-all hover:text-rose-500 hover:bg-rose-50">
              <X size={14} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Single ID-card side uploader (front or back).
 * Accepts JPG/PNG, converts to base64, and shows a live thumbnail preview.
 */
const IdCardUploader = ({ label, hint, fieldKey, entry, onUpload, onRemove }) => {
  const inputId  = `idcard-${fieldKey}`;
  const isUrl    = isUrlEntry(entry);
  const isBase64 = isBase64Entry(entry);
  const hasFile  = isUrl || isBase64;
  const previewSrc = useEntryPreviewUrl(entry);

  return (
    <div className={`relative rounded-xl border-2 transition-all duration-200 overflow-hidden ${
      hasFile
        ? isUrl
          ? "border-[#FFD221]/40 bg-[#FFD221]/[0.05]"
          : "border-[#FFD221]/50 bg-[#FFD221]/[0.08]"
        : "border-dashed border-gray-200 hover:border-[#FFD221]/50 hover:bg-[#fffdf4]"
    }`}>
      {isBase64 ? (
        /* ── Live preview: full image visible (letterboxed) inside the preview area ── */
        <div className="relative">
          <div className="flex justify-center items-center w-full h-32 bg-slate-100/95 sm:h-36">
            <img
              src={previewSrc}
              alt={label}
              className="object-contain object-center max-w-full max-h-full"
            />
          </div>
          <span className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-md bg-black/70 text-[9px] font-black text-white uppercase tracking-wide shadow-sm">
            {label}
          </span>
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 via-black/35 to-transparent px-2.5 pb-2 pt-8">
            <p className="text-[10px] font-semibold text-white/90 truncate flex-1">{entry.name}</p>
            <div className="flex gap-1 shrink-0">
              <label htmlFor={inputId}
                className="cursor-pointer flex items-center gap-0.5 px-2 py-1 rounded-lg bg-white/20 backdrop-blur-sm hover:bg-white/35 text-[9px] font-bold text-white transition-all">
                <RotateCcw size={8} />
                <input id={inputId} type="file" accept=".jpg,.jpeg,.png" className="hidden"
                  onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} />
              </label>
              <button type="button" onClick={onRemove}
                className="flex items-center px-1.5 py-1 rounded-lg bg-rose-500/80 hover:bg-rose-500 backdrop-blur-sm transition-all">
                <X size={9} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      ) : isUrl ? (
        /* ── Saved on server: same letterboxed preview as new uploads ── */
        <div className="relative">
          <div className="flex justify-center items-center w-full h-32 bg-slate-100/95 sm:h-36">
            <img
              src={entry.url}
              alt={label}
              className="object-contain object-center max-w-full max-h-full"
            />
          </div>
          <span className="absolute top-1.5 left-1.5 flex items-center gap-1 rounded-md bg-black/75 px-2 py-0.5 text-[9px] font-black uppercase tracking-wide text-white shadow-sm">
            <CheckCircle2 size={10} className="shrink-0" />
            {label}
          </span>
          <p className="absolute top-1.5 right-1.5 max-w-[55%] truncate rounded-md bg-white/90 px-2 py-0.5 text-[9px] font-medium text-gray-600 shadow-sm backdrop-blur-sm">
            {entry.name}
          </p>
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 via-black/35 to-transparent px-2.5 pb-2 pt-8">
            <p className="truncate flex-1 text-[10px] font-semibold text-white/95">Saved on server — replace to update</p>
            <div className="flex gap-1 shrink-0">
              <label htmlFor={inputId}
                className="cursor-pointer flex items-center gap-0.5 rounded-lg bg-white/20 px-2 py-1 text-[9px] font-bold text-white backdrop-blur-sm transition-all hover:bg-white/35">
                <RotateCcw size={8} />
                Replace
                <input id={inputId} type="file" accept=".jpg,.jpeg,.png" className="hidden"
                  onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} />
              </label>
              <button type="button" onClick={onRemove}
                className="flex items-center rounded-lg bg-rose-500/80 px-1.5 py-1 backdrop-blur-sm transition-all hover:bg-rose-500">
                <X size={9} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ── Empty / awaiting upload ── */
        <label htmlFor={inputId} className="flex flex-col gap-2 justify-center items-center py-5 cursor-pointer group">
          <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-[#FFD221]/15 flex items-center justify-center transition-all">
            <Upload size={16} className="text-gray-400 group-hover:text-[#9a7b00] transition-all" />
          </div>
          <div className="px-2 text-center">
            <p className="text-xs font-semibold text-gray-600 group-hover:text-[#9a7b00] transition-colors">{label}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">{hint ?? "JPG or PNG · max 5 MB"}</p>
          </div>
          <input id={inputId} type="file" accept=".jpg,.jpeg,.png" className="hidden"
            onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} />
        </label>
      )}
    </div>
  );
};

const Step6 = ({ form, update, errors, editableDocIds = null, flaggedReason = "", docStatuses = {}, visibleDocIds = null }) => {
  const visibleDocs = visibleDocIds ? REQUIRED_DOCS.filter((doc) => visibleDocIds.includes(doc.id)) : REQUIRED_DOCS;
  const uploaded = Object.keys(form.documents).length;
  const editableDocSet = useMemo(() => (Array.isArray(editableDocIds) ? new Set(editableDocIds) : null), [editableDocIds]);
  const isFlaggedDocReview = Boolean(editableDocSet);
  const [uploadError, setUploadError] = useState("");

  const validateDocumentFile = (doc, file) => {
    const allowedMimeTypes = Array.isArray(doc?.allowedMimeTypes) ? doc.allowedMimeTypes : [];
    const allowedExtensions = String(doc?.accept || "")
      .split(",")
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);

    const mime = String(file?.type || "").toLowerCase();
    const ext = (() => {
      const name = String(file?.name || "");
      const idx = name.lastIndexOf(".");
      return idx >= 0 ? name.slice(idx).toLowerCase() : "";
    })();

    const mimeOk = mime ? allowedMimeTypes.includes(mime) : false;
    const extOk = ext ? allowedExtensions.includes(ext) : false;
    if (mimeOk || extOk) return "";

    const expected = doc?.accept === ".pdf" ? "PDF only" : "PDF, JPEG, or PNG";
    return `${doc?.label || "Document"} must be uploaded as ${expected}.`;
  };

  const handleUpload = (id, file) => {
    if (editableDocSet && !editableDocSet.has(id)) return;
    const doc = REQUIRED_DOCS.find((d) => d.id === id);
    const validationMessage = validateDocumentFile(doc, file);
    if (validationMessage) {
      setUploadError(validationMessage);
      return;
    }
    setUploadError("");
    fileToDocEntry(file)
      .then((entry) => update("documents", (prev) => ({ ...(prev || {}), [id]: entry })))
      .catch(() => {/* file read failed — silently ignore */});
  };

  const handleRemove = (id) => {
    if (editableDocSet && !editableDocSet.has(id)) return;
    setUploadError("");
    const updated = { ...form.documents };
    delete updated[id];
    update("documents", updated);
  };

  return (
    <div id="step6-documents" className="space-y-4">
      <div className="rounded-2xl border border-[#FFD221]/35 bg-[#FFD221]/[0.06] px-4 py-3 sm:px-4 sm:py-3.5">
        <p className="text-xs font-semibold leading-snug text-gray-800">
          <span className="text-[#9a7b00]">Continue requires every mandatory document below.</span>{" "}
          You can still use <span className="font-bold">Save draft</span> in the footer to keep your progress from
          earlier steps even if uploads are not finished yet.
        </p>
      </div>
      {isFlaggedDocReview && (
        <div className="px-4 py-3 bg-amber-50 rounded-2xl border border-amber-200">
          <p className="text-xs font-semibold leading-snug text-amber-900">
            Upload replacements only for documents marked failed or pending. Passed documents are locked.
            {flaggedReason ? ` Reason: ${flaggedReason}` : ""}
          </p>
          <p className="mt-1 text-[11px] text-amber-700">
            Need to change a locked file?{" "}
            <Link to="/contact" className="font-bold hover:underline">
              Contact us
            </Link>
            .
          </p>
        </div>
      )}

      {/* Progress indicator */}
      <div className="flex justify-between items-center px-1 pb-1">
        <p className="text-xs text-gray-500">
          {uploaded} of {visibleDocs.length} documents uploaded
        </p>
        <div className="flex gap-1">
          {visibleDocs.map((d) => (
            <div key={d.id}
              className={`w-5 h-1.5 rounded-full transition-all duration-300 ${form.documents[d.id] ? "bg-[#FFD221]" : "bg-gray-200"}`}
            />
          ))}
        </div>
      </div>

        {visibleDocs.map((doc) => {
        const docIsLocked = Boolean(editableDocSet) && !editableDocSet.has(doc.id);
        const rawDocStatus = isFlaggedDocReview
          ? String(docStatuses?.[doc.id] || "").toLowerCase() || "pending"
          : null;
        return (
          <DocUploader
            key={doc.id}
            doc={doc}
            file={form.documents[doc.id]}
            onUpload={handleUpload}
            onRemove={handleRemove}
            isLocked={docIsLocked}
            flagDocStatus={isFlaggedDocReview ? (docIsLocked ? "pass" : rawDocStatus) : null}
          />
        );
      })}

      <FieldError msg={uploadError || errors.documents} />

      <div className="flex gap-3 items-start p-4 mt-2 bg-sky-50 rounded-xl border border-sky-100">
        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-sky-500" />
        <p className="text-xs leading-relaxed text-sky-700">
          All documents are encrypted with AES-256 and accessible only to authorised compliance reviewers. Your data is never shared with third parties.
        </p>
      </div>
    </div>
  );
};

function WhatsAppGlyph({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function CommunicationChannelIcon({ channel }) {
  const ring =
    "flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] transition-transform duration-300 group-hover:scale-[1.05]";
  if (channel === "Email") {
    return (
      <div className={`text-sky-600 bg-gradient-to-br from-sky-100 to-sky-50 ${ring}`}>
        <Mail className="w-9 h-9" strokeWidth={1.65} />
      </div>
    );
  }
  if (channel === "SMS") {
    return (
      <div className={`text-violet-600 bg-gradient-to-br from-violet-100 to-violet-50 ${ring}`}>
        <MessageSquare className="w-9 h-9" strokeWidth={1.65} />
      </div>
    );
  }
  if (channel === "WhatsApp") {
    return (
      <div className={`text-emerald-600 bg-gradient-to-br from-emerald-100 to-emerald-50 ${ring}`}>
        <WhatsAppGlyph className="w-9 h-9" />
      </div>
    );
  }
  return null;
}

/** Parse stored budget string e.g. "3000 - 3000000" or legacy "GHS 3,000 - GHS 3,000,000". */
const parseBudgetPair = (value) => {
  const nums = String(value || "")
    .match(/\d[\d,]*/g)
    ?.map((item) => Number(item.replace(/,/g, "")))
    .filter((item) => Number.isFinite(item));
  if (!nums?.length) return { from: "", to: "" };
  if (nums.length === 1) return { from: nums[0], to: nums[0] };
  return { from: Math.min(nums[0], nums[1]), to: Math.max(nums[0], nums[1]) };
};

const formatBudgetRangePayload = (fromNum, toNum) => {
  if (!Number.isFinite(fromNum) || !Number.isFinite(toNum)) return "";
  const lo = Math.min(fromNum, toNum);
  const hi = Math.max(fromNum, toNum);
  return `${lo} - ${hi}`;
};

const BudgetRangeInputs = ({ value, onChange }) => {
  const parsed = parseBudgetPair(value);
  const [fromStr, setFromStr] = useState(parsed.from === "" ? "" : String(parsed.from));
  const [toStr, setToStr] = useState(parsed.to === "" ? "" : String(parsed.to));
  const lastCommittedRef = useRef(value || "");

  useEffect(() => {
    if ((value || "") === lastCommittedRef.current) return;
    const next = parseBudgetPair(value);
    setFromStr(next.from === "" ? "" : String(next.from));
    setToStr(next.to === "" ? "" : String(next.to));
  }, [value]);

  const tryCommit = (fromRaw, toRaw) => {
    const f = fromRaw === "" ? NaN : Number(fromRaw);
    const t = toRaw === "" ? NaN : Number(toRaw);
    if (Number.isFinite(f) && Number.isFinite(t)) {
      const payload = formatBudgetRangePayload(f, t);
      lastCommittedRef.current = payload;
      onChange(payload);
    }
  };

  return (
    <div id="step7-budget-range">
      <Label>Budget Range</Label>
      <p className="mb-2 mt-1 text-[11px] leading-relaxed text-gray-400">
        Enter amounts in GHS (e.g. from <span className="font-semibold text-gray-600">3000</span> to{" "}
        <span className="font-semibold text-gray-600">3000000</span>).
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          id="step7-budget-from"
          label="From (GHS)"
          type="number"
          inputMode="numeric"
          min={0}
          step="any"
          placeholder="3000"
          value={fromStr}
          onChange={(e) => {
            const next = e.target.value;
            setFromStr(next);
            tryCommit(next, toStr);
          }}
        />
        <Input
          id="step7-budget-to"
          label="To (GHS)"
          type="number"
          inputMode="numeric"
          min={0}
          step="any"
          placeholder="3000000"
          value={toStr}
          onChange={(e) => {
            const next = e.target.value;
            setToStr(next);
            tryCommit(fromStr, next);
          }}
        />
      </div>
    </div>
  );
};

const Step7 = ({ form, update, errors }) => {
  const toggleCommercialInterest = (value) => {
    const curr = Array.isArray(form.commercialInterests) ? form.commercialInterests : [];
    update("commercialInterests", curr.includes(value) ? curr.filter((item) => item !== value) : [...curr, value]);
  };

  const toggleCommChannel = (value) => {
    const curr = Array.isArray(form.commChannels) ? form.commChannels : [];
    update("commChannels", curr.includes(value) ? curr.filter((item) => item !== value) : [...curr, value]);
  };

  return (
    <div className="space-y-8">
      <div id="step7-engagement-model">
        <SingleSelectCards
          label="Engagement Model"
          options={ENGAGEMENT_MODEL_OPTIONS}
          selected={form.engagementModel}
          onSelect={(value) => update("engagementModel", value)}
          optionsClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        />
      </div>

      <div id="step7-commercial-interests">
        <MultiSelect label="Interested In" required options={COMMERCIAL_INTEREST_OPTIONS}
          selected={form.commercialInterests || []} onToggle={toggleCommercialInterest} error={errors?.commercialInterests}
          optionsContainerClass="grid grid-cols-1 sm:grid-cols-2 gap-3"
          optionClassName="w-full justify-center py-4 text-sm" />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <BudgetRangeInputs value={form.budgetRange} onChange={(next) => update("budgetRange", next)} />
        <SingleSelectCards
          id="step7-payment-method"
          label="Payment Method"
          required
          options={PAYMENT_METHODS}
          selected={form.paymentMethod}
          onSelect={(value) => update("paymentMethod", value)}
          error={errors?.paymentMethod}
          optionsClassName="grid grid-cols-1 gap-3"
        />
      </div>

      <div id="step7-comm-channels">
        <p className="mb-1.5 text-xs font-semibold tracking-wide text-gray-600 uppercase">
          Preferred communication channels
        </p>
        <p className="mb-3 text-xs text-gray-500">Optional — how should InfoBoxx reach your team?</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {COMM_CHANNEL_OPTIONS.map((channel) => {
            const active = (form.commChannels || []).includes(channel.value);
            return (
              <button
                key={channel.value}
                type="button"
                onClick={() => toggleCommChannel(channel.value)}
                className={`rounded-2xl border p-4 text-left transition ${
                  active
                    ? "border-[#FFD221] bg-[#FFD221]/15 shadow-sm"
                    : "border-gray-200 bg-white hover:border-[#FFD221]/50 hover:bg-[#FFD221]/[0.06]"
                }`}
              >
                <div className="mb-3 flex items-center justify-between gap-2">
                  <CommunicationChannelIcon channel={channel.value} />
                  {active ? <Check size={14} className="text-[#9a7b00]" strokeWidth={3} /> : null}
                </div>
                <p className="text-sm font-bold text-gray-900">{channel.label}</p>
                <p className="mt-1 text-[11px] leading-relaxed text-gray-500">{channel.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div id="step7-contract-upload">
        <Label>Contract Agreement Upload</Label>
        <p className="mb-3 text-xs leading-relaxed text-gray-500">
          Optional. Upload an existing draft agreement if your organisation already has one.
        </p>
        <DocUploader
          doc={REQUIRED_DOCS.find((doc) => doc.id === "contract_agreement")}
          file={form.documents.contract_agreement}
          onUpload={(id, file) => fileToDocEntry(file).then((entry) => update("documents", (prev) => ({ ...(prev || {}), [id]: entry }))).catch(() => {})}
          onRemove={(id) => {
            const updated = { ...form.documents };
            delete updated[id];
            update("documents", updated);
          }}
        />
      </div>
    </div>
  );
};

const Step8 = ({ form, update, errors }) => (
  <div className="space-y-4">
    <p className="mb-5 text-sm leading-relaxed text-gray-500">
      Please read each declaration carefully before signing. Your digital signature confirms your agreement on behalf of your organisation.
    </p>

    <div id="step8-declaration-accuracy">
    <CheckboxField
      label="I declare that all information provided in this application is accurate and complete to the best of my knowledge."
      checked={form.declarationAccuracy}
      onChange={() => update("declarationAccuracy", !form.declarationAccuracy)}
    />
    </div>
    <FieldError msg={errors.declarationAccuracy} />

    <div id="step8-consent-data-processing">
    <CheckboxField
      label="I consent to InfoBoxx using this data for platform operations, verification, content coordination, and lead management."
      checked={form.consentDataProcessing}
      onChange={() => update("consentDataProcessing", !form.consentDataProcessing)}
    />
    </div>
    <FieldError msg={errors.consentDataProcessing} />

    <div id="step8-accept-terms">
    <CheckboxField
      label={
        <span>
          I have read and accept the{" "}
          <span className="text-[#9a7b00] underline font-semibold cursor-pointer hover:text-black">InfoBoxx Terms & Conditions</span>{" "}
          and the{" "}
          <span className="text-[#9a7b00] underline font-semibold cursor-pointer hover:text-black">Partner Agreement</span>.
        </span>
      }
      checked={form.acceptTerms}
      onChange={() => update("acceptTerms", !form.acceptTerms)}
    />
    </div>
    <FieldError msg={errors.acceptTerms} />

    <div className="grid grid-cols-1 gap-5 pt-3 sm:grid-cols-2">
      <Input id="step8-signature-name" label="Digital Signature (Full Name)" required placeholder="Type your full name"
        value={form.digitalSignatureName}
        onChange={(e) => update("digitalSignatureName", e.target.value)}
        error={errors.digitalSignatureName} />
      <Input label="Date" type="date"
        value={form.digitalSignatureDate}
        onChange={(e) => update("digitalSignatureDate", e.target.value)} />
    </div>

    <AnimatePresence>
      {form.digitalSignatureName && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="rounded-2xl border border-[#FFD221]/40 bg-gradient-to-br from-[#FFD221]/[0.08] via-white to-[#FFD221]/[0.03] p-5 shadow-[0_10px_30px_rgba(255,210,33,0.12)]"
        >
          <div className="flex gap-3 justify-between items-center mb-3">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Electronic signature preview</p>
            <span className="rounded-full border border-[#FFD221]/40 bg-white px-2.5 py-0.5 text-[10px] font-bold text-[#9a7b00]">
              eSigned
            </span>
          </div>

          <div className="relative overflow-hidden rounded-xl border border-[#FFD221]/30 bg-white/90 px-4 py-5">
            <div
              className="absolute inset-0 pointer-events-none opacity-35"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(to bottom, transparent 0px, transparent 29px, rgba(15, 23, 42, 0.08) 29px, rgba(15, 23, 42, 0.08) 30px)",
              }}
            />
            <div className="relative">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Signed by</p>
              <p
                className="mt-2 text-[2rem] leading-none text-gray-900 sm:text-[2.45rem]"
                style={{
                  fontFamily: "\"Segoe Script\", \"Lucida Handwriting\", \"Brush Script MT\", cursive",
                  transform: "rotate(-1.8deg)",
                }}
              >
                {form.digitalSignatureName}
              </p>
              <div className="mt-3 h-px w-full bg-gray-200" />
              <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-gray-500">
                <span className="font-medium">Date: {form.digitalSignatureDate || "Not set"}</span>
                <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full border border-[#FFD221]/40 bg-[#FFD221]/20 px-2 text-[10px] font-bold text-[#9a7b00]">
                  {form.digitalSignatureName
                    .trim()
                    .split(/\s+/)
                    .map((namePart) => namePart[0])
                    .join("")
                    .slice(0, 3)
                    .toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* ══════════════════════════════════════════════════════════════
   VALIDATION
   - validateStep(step, form): errors for a single step (used by full-form validation).
   - validateCompanyInfo(form): step-1 only — required to save draft.
   - validateFullForm(form): all steps — required to submit.
══════════════════════════════════════════════════════════════ */

const validateStep = (step, form) => {
  const errs = {};
  const s = (v) => (v ?? "").toString().trim();
  const websiteValue = s(form.websiteUrl);
  if (step === 1) {
    const logoOk = isUploadEntry(form?.logo);
    if (!logoOk) errs.logo = "Please upload your company logo";
    if (!s(form.legalName))              errs.legalName = "Required";
    if (!form.companyType)                errs.companyType = "Required";
    if (form.companyType === "Other" && !s(form.companyTypeCustom)) {
      errs.companyTypeCustom = "Please describe your company type";
    }
    if (!s(form.yearIncorporated))        errs.yearIncorporated = "Required";
    if (!s(form.countryOfIncorporation))  errs.countryOfIncorporation = "Required";
    if (!s(form.registeredAddress))       errs.registeredAddress = "Required";
    if (!s(form.region))                  errs.region = "Select a region";
    if (!s(form.city))                    errs.city = "Required";
    if (websiteValue && !isValidWebsiteUrl(websiteValue)) {
      errs.websiteUrl = "Use a valid format: https://google.com, https://www.google.com, or www.google.com";
    }
  }
  if (step === 2) {
    const pc = form.primaryContact || {};
    const trim = (v) => (v ?? "").toString().trim();
    const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trim(e));

    if (!trim(pc.fullName)) errs.primaryContactFullName = "Primary contact full name is required";
    if (!trim(pc.jobTitle)) errs.primaryContactJobTitle = "Position / role is required";
    if (!s(form.corporatePhone)) errs.corporatePhone = "Required";
    if (!s(form.corporateEmail)) errs.corporateEmail = "Required";
    else if (!emailOk(form.corporateEmail)) errs.corporateEmail = "Invalid email address";
    if (!trim(pc.email)) errs.primaryContactEmail = "Primary contact email is required";
    else if (!emailOk(pc.email)) errs.primaryContactEmail = "Enter a valid email address";
    if (!trim(pc.phone)) errs.primaryContactPhone = "Primary contact phone is required";

    // Secondary contacts are optional, but once added they must be complete.
    asArray(form.secondaryContacts).forEach((sc, index) => {
      const isPassport = String(sc?.idType || "").toLowerCase().includes("passport");
      if (!trim(sc?.fullName)) errs[`secondaryContact${index}FullName`] = "Full name is required";
      if (!trim(sc?.jobTitle)) errs[`secondaryContact${index}JobTitle`] = "Position / role is required";
      if (!trim(sc?.email)) errs[`secondaryContact${index}Email`] = "Email is required";
      else if (!emailOk(sc.email)) errs[`secondaryContact${index}Email`] = "Enter a valid email address";
      if (!trim(sc?.phone)) errs[`secondaryContact${index}Phone`] = "Phone is required";
      if (!trim(sc?.idType)) errs[`secondaryContact${index}IdType`] = "Select an ID type";
      if (!trim(sc?.idNumber)) errs[`secondaryContact${index}IdNumber`] = "ID number is required";
      if (!sc?.idFront) errs[`secondaryContact${index}IdFront`] = "Upload the front of the ID document";
      if (!isPassport && !sc?.idBack) errs[`secondaryContact${index}IdBack`] = "Upload the back of the ID document";
    });
  }
  if (step === 3) {
    const mandatory = ["cert_incorporation", "cert_commence"];
    const missing = mandatory.filter((id) => !form.documents[id]);
    if (missing.length) errs.documents = "Please upload the required business verification documents";
    if (!s(form.registrationNumber))      errs.registrationNumber = "Required";
    if (!s(form.tin))                     errs.tin = "Required";
    const pc = form.primaryContact || {};
    const isPassportId = String(pc.idType || "").toLowerCase().includes("passport");
    if (!pc.idType) errs.primaryContactIdType = "Select the primary contact's ID type";
    if (!s(pc.idNumber)) errs.primaryContactIdNumber = "ID number is required";
    if (!pc.idFront) errs.primaryContactIdFront = "Upload the front of the ID document";
    if (!isPassportId && !pc.idBack) errs.primaryContactIdBack = "Upload the back of the ID document";
  }
  if (step === 4) {
    if (!form.primaryCategories.length)  errs.primaryCategories = "Select a primary category";
    if (!form.subCategories.length)      errs.subCategories = "Select at least one subcategory";
    if (!form.specificServices?.length)  errs.specificServices = "Select at least one service";
    if (!s(form.yearsOfExperience))      errs.yearsOfExperience = "Required";
    if (!form.coverageAreas.length)      errs.coverageAreas = "Select at least one region";
    if (!form.serviceMode)               errs.serviceMode = "Please select a service mode";
    if (!form.preferredContactMethod)    errs.preferredContactMethod = "Required";
    if (!form.acceptsInstantLeads)       errs.acceptsInstantLeads = "Required";
    if (!form.responseTimeSla)           errs.responseTimeSla = "Required";
    if (!s(form.appointmentAvailability)) errs.appointmentAvailability = "Required";
  }
  if (step === 5) {
    if (!form.commercialInterests?.length) errs.commercialInterests = "Select at least one commercial model";
    if (!form.paymentMethod)               errs.paymentMethod = "Select a payment method";
  }
  if (step === 6) {
    if (!form.declarationAccuracy)     errs.declarationAccuracy = "Required";
    if (!form.consentDataProcessing)   errs.consentDataProcessing = "Required";
    if (!form.acceptTerms)             errs.acceptTerms = "Required";
    if (!s(form.digitalSignatureName)) errs.digitalSignatureName = "Please type your full name to sign";
  }
  return errs;
};

const getStepStarted = (step, form) => {
  const hasText = (value) => Boolean((value ?? "").toString().trim());
  const hasDocEntry = (entry) => Boolean(entry);

  if (step === 1) {
    return Boolean(
      form.logo ||
      hasText(form.legalName) ||
      hasText(form.tradingName) ||
      hasText(form.companyType) ||
      hasText(form.companyTypeCustom) ||
      hasText(form.yearIncorporated) ||
      hasText(form.businessDescription) ||
      hasText(form.registeredAddress) ||
      hasText(form.region) ||
      hasText(form.city) ||
      hasText(form.country) ||
      hasText(form.gpsLocation) ||
      hasText(form.websiteUrl)
    );
  }
  if (step === 2) {
    const pc = form.primaryContact || {};
    return Boolean(
      hasText(pc.fullName) ||
      hasText(pc.jobTitle) ||
      hasText(pc.email) ||
      hasText(pc.phone) ||
      hasText(pc.alternatePhone) ||
      hasText(form.corporatePhone) ||
      hasText(form.corporateEmail)
    );
  }
  if (step === 3) {
    const pc = form.primaryContact || {};
    return Boolean(
      Object.values(form.documents || {}).some(Boolean) ||
      hasText(form.registrationNumber) ||
      hasText(form.tin) ||
      hasText(pc.idType) ||
      hasText(pc.idNumber) ||
      hasDocEntry(pc.idFront) ||
      hasDocEntry(pc.idBack)
    );
  }
  if (step === 4) {
    return Boolean(
      (form.primaryCategories || []).length ||
      (form.subCategories || []).length ||
      (form.specificServices || []).length ||
      (form.coverageAreas || []).length ||
      hasText(form.yearsOfExperience) ||
      hasText(form.serviceMode) ||
      hasText(form.preferredContactMethod),
    );
  }
  if (step === 5) {
    return Boolean(
      (form.commercialInterests || []).length ||
      hasText(form.budgetRange) ||
      hasText(form.paymentMethod) ||
      hasDocEntry(form.documents?.contract_agreement)
    );
  }
  return false;
};

const isValidWebsiteUrl = (value) => {
  // Accept only: https://domain.tld, https://www.domain.tld, or www.domain.tld
  if (!value) return true;
  const hasAllowedPrefix = value.startsWith("https://") || value.startsWith("www.");
  if (!hasAllowedPrefix) return false;
  const candidate = value.startsWith("www.") ? `https://${value}` : value;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== "https:") return false;
    const host = parsed.hostname;
    if (!host.includes(".")) return false;
    const labels = host.split(".").filter(Boolean);
    if (labels.length < 2) return false;
    const tld = labels[labels.length - 1];
    return /^[a-z]{2,}$/i.test(tld);
  } catch {
    return false;
  }
};

const STEP_FIELD_SCROLL_TARGETS = {
  1: {
    logo: "step1-logo",
    legalName: "step1-legal-name",
    tradingName: "step1-trading-name",
    companyType: "step1-company-type",
    companyTypeCustom: "step1-company-type",
    yearIncorporated: "step1-year-incorporated",
    businessDescription: "step1-business-description",
    registeredAddress: "step2-registered-address",
    region: "step2-region",
    city: "step2-city",
  },
  2: {
    primaryContactFullName: "step2-primary-full-name",
    primaryContactJobTitle: "step2-primary-role",
    primaryContactEmail: "step2-primary-email",
    primaryContactPhone: "step2-primary-phone",
    corporatePhone: "step2-primary-phone",
    corporateEmail: "step2-primary-email",
  },
  3: {
    registrationNumber: "step3-registration-number",
    tin: "step3-tin",
    documents: "step6-documents",
    primaryContactIdType: "step3-identity",
    primaryContactIdNumber: "step3-id-number",
    primaryContactIdFront: "step3-identity",
    primaryContactIdBack: "step3-identity",
  },
  4: {
    primaryCategories: "step4-primary-categories",
    subCategories: "step4-sub-categories",
    specificServices: "step4-specific-services",
    yearsOfExperience: "step4-years-experience",
    coverageAreas: "step4-coverage-areas",
    serviceMode: "step4-service-mode",
    preferredContactMethod: "step4-preferred-contact",
    responseTimeSla: "step4-response-time-sla",
    acceptsInstantLeads: "step4-instant-leads",
    appointmentAvailability: "step4-appointment-availability",
  },
  5: {
    engagementModel: "step7-engagement-model",
    commercialInterests: "step7-engagement-model",
    paymentMethod: "step7-payment-method",
  },
  6: {
    declarationAccuracy: "step8-declaration-accuracy",
    consentDataProcessing: "step8-consent-data-processing",
    acceptTerms: "step8-accept-terms",
    digitalSignatureName: "step8-signature-name",
  },
};

function scrollToFirstMissingField(step, stepErrs) {
  const firstMissingField = Object.keys(stepErrs)[0];
  let targetId = STEP_FIELD_SCROLL_TARGETS?.[step]?.[firstMissingField];
  if (!targetId && (step === 2 || step === 5) && /^secondaryContact\d+/.test(firstMissingField || "")) {
    targetId = "step2-secondary-contacts";
  }
  if (!targetId) return;
  const target = document.getElementById(targetId);
  if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
}

/** Company info (step 1) only — must pass before saving draft. */
const validateCompanyInfo = (form) => validateStep(1, form);

/** All steps — must pass before final submit. Returns { allErrors, firstInvalidStep }. */
const validateFullForm = (form) => {
  let firstInvalidStep = null;
  const allErrors = {};
  for (let s = 1; s <= STEPS.length; s++) {
    const stepErrs = validateStep(s, form);
    if (Object.keys(stepErrs).length) {
      if (firstInvalidStep == null) firstInvalidStep = s;
      Object.assign(allErrors, stepErrs);
    }
  }
  return { allErrors, firstInvalidStep };
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Completion metric based on required fields being filled.
 * This replaces the old "step-based" progress (which didn't reflect user input).
 */
const computeCompletion = (f) => {
  const primaryIdIsPassport = String(f.primaryContact?.idType || "").toLowerCase().includes("passport");
  const checks = [
    () => isUploadEntry(f.logo),
    () => !!(f.legalName || "").trim(),
    () => !!f.companyType,
    () =>
      f.companyType !== "Other" ||
      !!(f.companyTypeCustom || "").trim(),
    () => !!(f.registrationNumber || "").trim(),
    () => !!(f.tin || "").trim(),
    () => !!(f.yearIncorporated || "").trim(),
    () => !!(f.countryOfIncorporation || "").trim(),
    () => !!(f.registeredAddress || "").trim(),
    () => !!(f.region || "").trim(),
    () => !!(f.city || "").trim(),
    () => !!(f.primaryContact?.fullName || "").trim(),
    () => !!(f.primaryContact?.jobTitle || "").trim(),
    () => !!(f.primaryContact?.phone || f.corporatePhone || "").trim(),
    () => !!(f.primaryContact?.email || f.corporateEmail || "").trim() && EMAIL_RE.test(f.primaryContact?.email || f.corporateEmail),
    () => !!f.documents?.cert_incorporation,
    () => !!f.documents?.cert_commence,
    () => !!f.primaryContact?.idType,
    () => !!(f.primaryContact?.idNumber || "").trim(),
    () => !!f.primaryContact?.idFront,
    () => primaryIdIsPassport || !!f.primaryContact?.idBack,
    () => Array.isArray(f.primaryCategories) && f.primaryCategories.length > 0,
    () => Array.isArray(f.subCategories) && f.subCategories.length > 0,
    () => Array.isArray(f.specificServices) && f.specificServices.length > 0,
    () => !!(f.yearsOfExperience || "").trim(),
    () => Array.isArray(f.coverageAreas) && f.coverageAreas.length > 0,
    () => !!f.serviceMode,
    () => !!f.preferredContactMethod,
    () => !!f.acceptsInstantLeads,
    () => !!f.responseTimeSla,
    () => !!(f.appointmentAvailability || "").trim(),
    () => Array.isArray(f.commercialInterests) && f.commercialInterests.length > 0,
    () => !!f.paymentMethod,
    () => f.declarationAccuracy === true,
    () => f.consentDataProcessing === true,
    () => f.acceptTerms === true,
    () => !!(f.digitalSignatureName || "").trim(),
  ];

  const total = checks.length;
  const filled = checks.reduce((acc, fn) => acc + (fn() ? 1 : 0), 0);
  const percent = total > 0 ? Math.round((filled / total) * 100) : 0;

  return { filled, total, percent };
};

const asArray = (value) => (Array.isArray(value) ? value : []);

/**
 * Serialize a single upload reference for the JSON payload:
 *   - base64 entry → the raw data-URL string
 *   - url entry    → the URL string (server already has the file)
 *   - null         → null
 */
const serializeUploadReference = (entry) => {
  if (!entry) return null;
  if (entry.type === "base64") return entry.data;
  if (entry.type === "url")    return entry.url;
  return null;
};

/** Serialize a contact object, converting idFront/idBack to strings. */
const normalizePhoneForPayload = (value) => {
  const raw = String(value ?? "").trim().replace(/^\+/, "");
  const digitsOnly = raw.replace(/\D/g, "");
  if (digitsOnly.length === 10 && digitsOnly.startsWith("0")) {
    return `233${digitsOnly.slice(1)}`;
  }
  return raw;
};

/** Serialize a contact object, converting idFront/idBack to strings. */
const serializeContact = (c) => {
  if (!c) return c;
  return {
    ...c,
    phone: normalizePhoneForPayload(c.phone),
    alternatePhone: normalizePhoneForPayload(c.alternatePhone),
    idFront: serializeUploadReference(c.idFront),
    idBack:  serializeUploadReference(c.idBack),
  };
};

const slugify = (value) =>
  String(value || "infoboxx-partner")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "infoboxx-partner";

const buildProgressData = (form) => {
  const stepStatus = {};
  for (let id = 1; id <= STEPS.length; id += 1) {
    stepStatus[`step_${id}`] = Object.keys(validateStep(id, form)).length === 0;
  }

  const documentEntries = REQUIRED_DOCS.reduce((acc, doc) => {
    const uploaded = Boolean(form.documents?.[doc.id]);
    const optional = doc.id === "contract_agreement";
    acc[doc.id] = {
      label: doc.label,
      uploaded,
      status: uploaded ? "pending" : optional ? "pass" : "fail",
    };
    return acc;
  }, {});

  const failedDocuments = Object.entries(documentEntries)
    .filter(([id, doc]) => id !== "contract_agreement" && !doc.uploaded)
    .map(([, doc]) => doc.label);
  const progressReason = failedDocuments.length
    ? `${failedDocuments.join(", ")} ${failedDocuments.length === 1 ? "is" : "are"} missing.`
    : "Application submitted for admin review.";

  return {
    steps: stepStatus,
    documents: documentEntries,
    failed_documents: failedDocuments,
    flag_reasons: failedDocuments.join(", "),
    progress_reason: progressReason,
  };
};

const buildPartnerPayload = (form, meta = {}) => {
  const primary = form.primaryContact || {};
  const serializedPrimary = serializeContact({
    ...primary,
    // Keep contact phone/email aligned with corporate fields used in Step 2.
    phone: pickFilled(primary.phone, form.corporatePhone),
    email: pickFilled(primary.email, form.corporateEmail),
  });

  const documentsMap = Object.fromEntries(
    Object.entries(form.documents || {}).map(([id, entry]) => {
      if (entry?.type === "url") return [id, entry.url];
      if (entry?.type === "base64") return [id, entry.data];
      if (typeof entry === "string" && entry.trim()) return [id, entry];
      return [id, null];
    }).filter(([, value]) => value != null),
  );

  const primaryCategory = asArray(form.primaryCategories)[0] || "";
  const primarySubCategory = asArray(form.subCategories)[0] || "";
  const companyEmail = textOrEmpty(form.corporateEmail);

  return {
    ...meta,
    slug: meta.slug || slugify(form.tradingName || form.legalName),
    approval_status: meta.approval_status || "in_progress",
    // Top-level columns used by admin draft_status validation (exists rules).
    company_email: companyEmail,
    category: primaryCategory,
    sub_category: primarySubCategory,
    progress_data: buildProgressData(form),
    form: {
      logo: serializeUploadReference(form.logo),
      legalName: textOrEmpty(form.legalName),
      tradingName: textOrEmpty(form.tradingName),
      companyType: form.companyType === "Other"
        ? (form.companyTypeCustom?.trim() || "Other")
        : textOrEmpty(form.companyType),
      yearIncorporated: textOrEmpty(form.yearIncorporated),
      countryOfIncorporation: textOrEmpty(form.countryOfIncorporation),
      registrationNumber: textOrEmpty(form.registrationNumber),
      tin: textOrEmpty(form.tin),
      businessDescription: textOrEmpty(form.businessDescription),
      registeredAddress: textOrEmpty(form.registeredAddress),
      operationalAddress: textOrEmpty(form.operationalAddress),
      city: textOrEmpty(form.city),
      region: textOrEmpty(form.region),
      country: textOrEmpty(form.country),
      gpsLocation: textOrEmpty(form.gpsLocation),
      corporatePhone: normalizePhoneForPayload(form.corporatePhone),
      corporateEmail: textOrEmpty(form.corporateEmail),
      websiteUrl: textOrEmpty(form.websiteUrl),
      primaryRegulator: form.primaryRegulator === "Other"
        ? (form.primaryRegulatorCustom?.trim() || "Other")
        : textOrEmpty(form.primaryRegulator),
      licenseNumber: textOrEmpty(form.licenseNumber),
      licenseClass: textOrEmpty(form.licenseClass),
      licenseIssueDate: textOrEmpty(form.licenseIssueDate),
      licenseExpiryDate: textOrEmpty(form.licenseExpiryDate),
      complianceAccepted: Boolean(form.complianceAccepted),
      primaryCategories: asArray(form.primaryCategories),
      subCategories: asArray(form.subCategories),
      specificServices: asArray(form.specificServices),
      customerSegments: asArray(form.customerSegments),
      coverageAreas: asArray(form.coverageAreas),
      digitalCapability: textOrEmpty(form.digitalCapability),
      yearsOfExperience: textOrEmpty(form.yearsOfExperience),
      numberOfStaff: textOrEmpty(form.numberOfStaff),
      serviceMode: textOrEmpty(form.serviceMode),
      languagesSpoken: asArray(form.languagesSpoken),
      willProvideContent: textOrEmpty(form.willProvideContent),
      contentTypes: asArray(form.contentTypes),
      hasExistingContent: textOrEmpty(form.hasExistingContent),
      contentUploadCapability: textOrEmpty(form.contentUploadCapability),
      preferredContactMethod: textOrEmpty(form.preferredContactMethod),
      acceptsInstantLeads: textOrEmpty(form.acceptsInstantLeads),
      responseTimeSla: textOrEmpty(form.responseTimeSla),
      primaryContact: serializedPrimary,
      secondaryContacts: asArray(form.secondaryContacts).map(serializeContact),
      // Always send an object map (never []). Backend array storage breaks resume.
      documents: documentsMap,
      engagementModel: textOrEmpty(form.engagementModel),
      commercialInterests: asArray(form.commercialInterests),
      budgetRange: textOrEmpty(form.budgetRange),
      paymentMethod: textOrEmpty(form.paymentMethod),
      commChannels: asArray(form.commChannels),
      appointmentAvailability: textOrEmpty(form.appointmentAvailability),
      declarationAccuracy: Boolean(form.declarationAccuracy),
      consentDataProcessing: Boolean(form.consentDataProcessing),
      acceptTerms: Boolean(form.acceptTerms),
      digitalSignatureName: textOrEmpty(form.digitalSignatureName),
      digitalSignatureDate: textOrEmpty(form.digitalSignatureDate),
    },
  };
};

const extractResponseEnvelope = (response) => response?.data ?? {};

const resolveDraftCode = (payload) =>
  payload?.draft_code ??
  payload?.draftCode ??
  payload?.data?.draft_code ??
  payload?.data?.draftCode ??
  payload?.data?.data?.draft_code ??
  payload?.data?.data?.draftCode ??
  payload?.result?.draft_code ??
  payload?.result?.draftCode ??
  "";

/** Server-assigned draft slug (UUID). Prefer this over slugifying the company name. */
const resolveDraftSlug = (payload) =>
  payload?.slug ??
  payload?.data?.slug ??
  payload?.data?.data?.slug ??
  payload?.result?.slug ??
  "";

const resolveDraftForm = (payload) =>
  payload?.form ??
  payload?.data?.form ??
  payload?.data?.data?.form ??
  payload?.result?.form ??
  payload?.draft ??
  payload?.data?.draft ??
  payload?.data?.data?.draft ??
  payload?.result?.draft ??
  null;

const resolveDraftStep = (payload) =>
  payload?.lastCompletedStep ??
  payload?.last_completed_step ??
  payload?.data?.lastCompletedStep ??
  payload?.data?.last_completed_step ??
  payload?.data?.data?.lastCompletedStep ??
  payload?.data?.data?.last_completed_step ??
  payload?.result?.lastCompletedStep ??
  payload?.result?.last_completed_step ??
  1;

/** Inner row from GET draft-by-code: `{ id, status, form, draft_code, ... }` */
const resolveDraftRecord = (payload) =>
  payload?.data?.data ??
  payload?.data ??
  null;

const EMPTY_FLAGGED_CONTEXT = {
  active: false,
  flagReason: "",
  progressSteps: {},
  documentStatuses: {},
};

/* ══════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════ */

const normalizePrimaryCategoriesForUi = (primaryCategories) => {
  const arr = Array.isArray(primaryCategories) ? primaryCategories : [];
  return arr.filter(Boolean);
};

/* ── Known option sets for "Select with Other" fields ── */
const KNOWN_COMPANY_TYPES = new Set(COMPANY_TYPE_OPTIONS.map((o) => o.value));
const KNOWN_REGULATORS    = new Set(["NIC", "BoG", "SEC", "NPRA", "Other"]);

/**
 * Given a backend value and the set of known <option> values, decide whether
 * to keep the value as-is or treat it as a custom "Other" entry.
 * Returns { main, custom } where `main` is what the <select> should show and
 * `custom` is the free-text field value.
 */
const resolveSelectWithOther = (value, knownValues) => {
  if (!value) return { main: "", custom: "" };
  if (knownValues.has(value)) return { main: value, custom: "" };
  return { main: "Other", custom: value };
};

/**
 * Walk the raw backend form object and correct any "Select with Other" fields
 * whose stored values are custom strings rather than known option keys.
 * Also accepts snake_case aliases and coerces nulls so resume never blanks the UI.
 */
const normalizeFormFromBackend = (rawForm = {}) => {
  const src = rawForm && typeof rawForm === "object" ? rawForm : {};
  const companyType = resolveSelectWithOther(
    pickFilled(src.companyType, src.company_type),
    KNOWN_COMPANY_TYPES,
  );
  const regulator = resolveSelectWithOther(
    pickFilled(src.primaryRegulator, src.primary_regulator),
    KNOWN_REGULATORS,
  );

  const primaryContactRaw = src.primaryContact || src.primary_contact || {};
  const secondaryRaw = src.secondaryContacts || src.secondary_contacts;

  return {
    ...INITIAL_FORM,
    ...src,
    logo: normalizeContactIdImage(pickFilled(src.logo, src.company_logo, src.logoUrl)),
    legalName: textOrEmpty(pickFilled(src.legalName, src.legal_name)),
    tradingName: textOrEmpty(pickFilled(src.tradingName, src.trading_name)),
    companyType: companyType.main,
    companyTypeCustom: companyType.custom || src.companyTypeCustom || "",
    yearIncorporated: textOrEmpty(pickFilled(src.yearIncorporated, src.year_incorporated)),
    countryOfIncorporation: textOrEmpty(
      pickFilled(src.countryOfIncorporation, src.country_of_incorporation, "Ghana"),
    ),
    registrationNumber: textOrEmpty(pickFilled(src.registrationNumber, src.registration_number)),
    tin: textOrEmpty(pickFilled(src.tin)),
    businessDescription: textOrEmpty(pickFilled(src.businessDescription, src.business_description)),
    registeredAddress: textOrEmpty(pickFilled(src.registeredAddress, src.registered_address)),
    operationalAddress: textOrEmpty(pickFilled(src.operationalAddress, src.operational_address)),
    city: textOrEmpty(pickFilled(src.city)),
    region: textOrEmpty(pickFilled(src.region)),
    country: textOrEmpty(pickFilled(src.country, "Ghana")),
    gpsLocation: textOrEmpty(pickFilled(src.gpsLocation, src.gps_location)),
    corporatePhone: textOrEmpty(pickFilled(src.corporatePhone, src.corporate_phone)),
    corporateEmail: textOrEmpty(pickFilled(src.corporateEmail, src.corporate_email)),
    websiteUrl: textOrEmpty(pickFilled(src.websiteUrl, src.website_url)),
    primaryRegulator: regulator.main,
    primaryRegulatorCustom: regulator.custom || src.primaryRegulatorCustom || "",
    licenseNumber: textOrEmpty(pickFilled(src.licenseNumber, src.license_number)),
    licenseClass: textOrEmpty(pickFilled(src.licenseClass, src.license_class)),
    licenseIssueDate: textOrEmpty(pickFilled(src.licenseIssueDate, src.license_issue_date)),
    licenseExpiryDate: textOrEmpty(pickFilled(src.licenseExpiryDate, src.license_expiry_date)),
    complianceAccepted: Boolean(src.complianceAccepted ?? src.compliance_accepted),
    primaryCategories: asArray(pickFilled(src.primaryCategories, src.primary_categories, [])),
    subCategories: asArray(pickFilled(src.subCategories, src.sub_categories, [])),
    specificServices: asArray(pickFilled(src.specificServices, src.specific_services, [])),
    customerSegments: asArray(pickFilled(src.customerSegments, src.customer_segments, [])),
    coverageAreas: asArray(pickFilled(src.coverageAreas, src.coverage_areas, [])),
    digitalCapability: textOrEmpty(pickFilled(src.digitalCapability, src.digital_capability)),
    yearsOfExperience: textOrEmpty(pickFilled(src.yearsOfExperience, src.years_of_experience)),
    numberOfStaff: textOrEmpty(pickFilled(src.numberOfStaff, src.number_of_staff)),
    serviceMode: textOrEmpty(pickFilled(src.serviceMode, src.service_mode)),
    languagesSpoken: asArray(pickFilled(src.languagesSpoken, src.languages_spoken, [])),
    willProvideContent: textOrEmpty(pickFilled(src.willProvideContent, src.will_provide_content)),
    contentTypes: asArray(pickFilled(src.contentTypes, src.content_types, [])),
    hasExistingContent: textOrEmpty(pickFilled(src.hasExistingContent, src.has_existing_content)),
    contentUploadCapability: textOrEmpty(
      pickFilled(src.contentUploadCapability, src.content_upload_capability),
    ),
    preferredContactMethod: textOrEmpty(
      pickFilled(src.preferredContactMethod, src.preferred_contact_method),
    ),
    acceptsInstantLeads: textOrEmpty(pickFilled(src.acceptsInstantLeads, src.accepts_instant_leads)),
    responseTimeSla: textOrEmpty(pickFilled(src.responseTimeSla, src.response_time_sla)),
    primaryContact: normalizeContactFromBackend({
      ...INITIAL_FORM.primaryContact,
      ...primaryContactRaw,
      fullName: textOrEmpty(pickFilled(primaryContactRaw.fullName, primaryContactRaw.full_name)),
      jobTitle: textOrEmpty(pickFilled(primaryContactRaw.jobTitle, primaryContactRaw.job_title)),
      email: textOrEmpty(pickFilled(primaryContactRaw.email, src.corporateEmail, src.corporate_email)),
      phone: textOrEmpty(pickFilled(primaryContactRaw.phone, src.corporatePhone, src.corporate_phone)),
      alternatePhone: textOrEmpty(
        pickFilled(primaryContactRaw.alternatePhone, primaryContactRaw.alternate_phone),
      ),
      idType: textOrEmpty(pickFilled(primaryContactRaw.idType, primaryContactRaw.id_type)),
      idNumber: textOrEmpty(pickFilled(primaryContactRaw.idNumber, primaryContactRaw.id_number)),
    }),
    secondaryContacts: asArray(secondaryRaw).map((c) =>
      normalizeContactFromBackend(c || INITIAL_FORM.primaryContact),
    ),
    documents: normalizeDocumentsFromBackend(src.documents),
    engagementModel: textOrEmpty(pickFilled(src.engagementModel, src.engagement_model)),
    commercialInterests: asArray(pickFilled(src.commercialInterests, src.commercial_interests, [])),
    budgetRange: textOrEmpty(pickFilled(src.budgetRange, src.budget_range)),
    paymentMethod: textOrEmpty(pickFilled(src.paymentMethod, src.payment_method)),
    commChannels: asArray(pickFilled(src.commChannels, src.comm_channels, [])),
    appointmentAvailability: textOrEmpty(
      pickFilled(src.appointmentAvailability, src.appointment_availability),
    ),
    declarationAccuracy: Boolean(src.declarationAccuracy ?? src.declaration_accuracy),
    consentDataProcessing: Boolean(src.consentDataProcessing ?? src.consent_data_processing),
    acceptTerms: Boolean(src.acceptTerms ?? src.accept_terms),
    digitalSignatureName: textOrEmpty(
      pickFilled(src.digitalSignatureName, src.digital_signature_name),
    ),
    digitalSignatureDate: textOrEmpty(
      pickFilled(src.digitalSignatureDate, src.digital_signature_date, INITIAL_FORM.digitalSignatureDate),
    ),
  };
};

/** Merge server-returned upload URLs into the local form without wiping filled fields. */
const mergeServerUploadsIntoForm = (localForm, serverFormRaw) => {
  if (!serverFormRaw || typeof serverFormRaw !== "object") return localForm;
  const server = normalizeFormFromBackend(serverFormRaw);
  const mergedDocs = { ...(localForm.documents || {}) };
  Object.entries(server.documents || {}).forEach(([id, entry]) => {
    if (entry?.type === "url") mergedDocs[id] = entry;
  });
  const serverLogo = server.logo;
  const localPrimary = localForm.primaryContact || {};
  const serverPrimary = server.primaryContact || {};
  return {
    ...localForm,
    logo: serverLogo?.type === "url" ? serverLogo : localForm.logo,
    documents: mergedDocs,
    primaryContact: {
      ...localPrimary,
      idFront:
        serverPrimary.idFront?.type === "url" ? serverPrimary.idFront : localPrimary.idFront,
      idBack:
        serverPrimary.idBack?.type === "url" ? serverPrimary.idBack : localPrimary.idBack,
    },
  };
};

const DRAFT_KEY = "infoboxx_partner_draft";

const clearPartnerDraftStorage = () => {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* quota / private mode */
  }
};

const stripBase64EntryForStorage = (entry) => (isUrlEntry(entry) ? entry : null);
const stripContactBase64ForStorage = (contact) => {
  if (!contact || typeof contact !== "object") return contact;
  return {
    ...contact,
    idFront: stripBase64EntryForStorage(contact.idFront),
    idBack: stripBase64EntryForStorage(contact.idBack),
  };
};

/** Persist draft as { lastCompletedStep, form, draftCode, draftSlug } for resume.
 *  URL document entries are preserved; base64 entries are stripped to avoid localStorage quota issues. */
const saveDraftToStorage = (step, form, { draftCode = "", draftSlug = "" } = {}) => {
  const safeDocuments = Object.fromEntries(
    Object.entries(form.documents || {}).filter(([, v]) => v?.type === "url")
  );
  const payload = {
    lastCompletedStep: step,
    draftCode: draftCode || "",
    draftSlug: draftSlug || "",
    form: {
      ...form,
      logo: stripBase64EntryForStorage(form.logo),
      primaryContact: stripContactBase64ForStorage(form.primaryContact),
      secondaryContacts: asArray(form.secondaryContacts).map(stripContactBase64ForStorage),
      documents: safeDocuments,
    },
  };
  localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
};

/** Load draft from storage. Returns { lastCompletedStep, form, draftCode, draftSlug } or null. */
const loadDraftFromStorage = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data && typeof data.form === "object") {
      return {
        lastCompletedStep: Math.min(Math.max(1, data.lastCompletedStep || 1), 8),
        draftCode: String(data.draftCode || data.draft_code || "").trim(),
        draftSlug: String(data.draftSlug || data.slug || "").trim(),
        form: normalizeFormFromBackend({
          ...INITIAL_FORM,
          ...data.form,
          documents: normalizeDocumentsFromBackend(data.form.documents),
        }),
      };
    }
    return { lastCompletedStep: 1, draftCode: "", draftSlug: "", form: { ...INITIAL_FORM, ...data, documents: {} } };
  } catch {
    return null;
  }
};

const hasDraftInStorage = () => !!localStorage.getItem(DRAFT_KEY);

const STEP_COMPONENTS = [Step1, Step2, Step3, Step4, Step7, Step8];

const PartnerOnboardPage = () => {
  const { draftCode: draftCodeFromUrl } = useParams();
  const navigate = useNavigate();
  const [flowChoice, setFlowChoice] = useState(null);
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftCode, setDraftCode] = useState("");
  /** Server UUID slug from get_draft / initiate — never replace with a name-based slug once set. */
  const [draftSlug, setDraftSlug] = useState("");
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resubmitDone, setResubmitDone] = useState(false);
  const [actionModal, setActionModal] = useState({
    open: false,
    status: "success",
    title: "",
    message: "",
    details: "",
    code: "",
    steps: [],
    purgeLocalDraftOnDismiss: false,
  });
  const [payloadPreview, setPayloadPreview] = useState({ open: false, text: "" });
  const [recoveryCode, setRecoveryCode] = useState("");
  const [codeLoadError, setCodeLoadError] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const [overlayDir, setOverlayDir] = useState(1);
  const [flaggedContext, setFlaggedContext] = useState(EMPTY_FLAGGED_CONTEXT);
  const fullValidation = useMemo(() => validateFullForm(form), [form]);
  const isFullFormComplete = Object.keys(fullValidation.allErrors).length === 0;
  const isStep1Complete = Object.keys(validateCompanyInfo(form)).length === 0;
  const isFlaggedResubmissionFlow = flaggedContext.active;
  const [flagReasonExpanded, setFlagReasonExpanded] = useState(false);

  /* ── Uniqueness checks for corporate phone & email (Step 1) ── */
  const [phoneCheck, setPhoneCheck] = useState({ status: "idle" });
  const [emailCheck, setEmailCheck] = useState({ status: "idle" });
  const phoneTimerRef = useRef(null);
  const emailTimerRef = useRef(null);
  const phoneRequestSeqRef = useRef(0);
  const emailRequestSeqRef = useRef(0);

  /* Per-step status summary used by sidebar + header in flagged mode */
  const stepFlaggedStatus = useMemo(() => {
    if (!isFlaggedResubmissionFlow) return {};
    const result = {};
    STEPS.forEach((s) => {
      const isApproved = Boolean(flaggedContext.progressSteps?.[`step_${s.id}`]);
      if (s.id === 3) {
        const failedDocIds = REQUIRED_DOCS.filter((doc) => doc.id !== "contract_agreement").filter((doc) => {
          const st = String(flaggedContext.documentStatuses?.[doc.id] || "").toLowerCase();
          return !st || st === "fail" || st === "failed" || st === "pending";
        });
        result[s.id] = failedDocIds.length > 0
          ? { type: "needs_action", count: failedDocIds.length }
          : { type: "approved" };
      } else if (isApproved) {
        result[s.id] = { type: "approved" };
      } else {
        result[s.id] = { type: "needs_review" };
      }
    });
    return result;
  }, [flaggedContext, isFlaggedResubmissionFlow]);

  /* Comma-separated flag reasons split into individual chips */
  const flagReasonChips = useMemo(() => {
    const raw = String(flaggedContext.flagReason || "");
    if (!raw) return [];
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }, [flaggedContext.flagReason]);

  const missingSubmitItems = useMemo(() => {
    const pretty = (k) => {
      const map = {
        logo: "Company logo",
        legalName: "Legal name",
        companyType: "Company type",
        companyTypeCustom: "Company type (other)",
        yearIncorporated: "Year incorporated",
        registrationNumber: "Registration number",
        tin: "TIN",
        businessDescription: "Business description",
        corporatePhone: "Corporate phone",
        corporateEmail: "Corporate email",
        registeredAddress: "Registered office address",
        city: "City / town",
        primaryRegulator: "Primary regulator",
        licenseNumber: "License number",
        licenseClass: "License category / class",
        licenseIssueDate: "License issue date",
        licenseExpiryDate: "License expiry date",
        complianceAccepted: "Compliance confirmation",
        primaryCategories: "Primary categories",
        subCategories: "Subcategory",
        specificServices: "Specific services offered",
        customerSegments: "Customer segments",
        coverageAreas: "Coverage areas",
        digitalCapability: "Digital capability",
        yearsOfExperience: "Years of experience",
        serviceMode: "Service mode",
        primaryContact: "Primary contact details",
        primaryContactFullName: "Primary contact — full name",
        primaryContactEmail: "Primary contact — email",
        primaryContactPhone: "Primary contact — phone",
        primaryContactIdType: "Primary contact — ID type",
        primaryContactIdFront: "Primary contact ID (front)",
        primaryContactIdBack: "Primary contact ID (back)",
        secondaryContactsRequired: "At least one secondary contact",
        documents: "Mandatory documents",
        engagementModel: "Engagement model",
        commChannels: "Communication channels",
        appointmentAvailability: "Appointment availability",
        declarationAccuracy: "Declaration accuracy",
        consentDataProcessing: "Data processing consent",
        acceptTerms: "Accept terms",
        digitalSignatureName: "Digital signature name",
      };
      if (map[k]) return map[k];

      const m = /^secondaryContact(\d+)(IdFront|IdBack|FullName|JobTitle|Email|Phone|IdType|IdNumber)$/.exec(k);
      if (m) {
        const idx = Number(m[1]) + 1;
        const part = m[2];
        if (part === "IdFront") return `Secondary contact ${idx} — ID (front)`;
        if (part === "IdBack") return `Secondary contact ${idx} — ID (back)`;
        if (part === "FullName") return `Secondary contact ${idx} — full name`;
        if (part === "JobTitle") return `Secondary contact ${idx} — job title`;
        if (part === "Email") return `Secondary contact ${idx} — email`;
        if (part === "Phone") return `Secondary contact ${idx} — phone`;
        if (part === "IdType") return `Secondary contact ${idx} — ID type`;
        if (part === "IdNumber") return `Secondary contact ${idx} — ID number`;
      }
      return k;
    };

    return Object.entries(fullValidation.allErrors)
      .map(([key, msg]) => ({ key, label: pretty(key), msg }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [fullValidation.allErrors]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [flowChoice, step]);

  const closeActionModal = useCallback(() => {
    setActionModal((prev) => {
      if (prev.purgeLocalDraftOnDismiss) clearPartnerDraftStorage();
      return { ...prev, open: false, purgeLocalDraftOnDismiss: false };
    });
  }, []);

  const applyTestPartnerFill = useCallback(() => {
    setForm(buildTestPartnerForm());
    setErrors({});
    setPhoneCheck({ status: "idle" });
    setEmailCheck({ status: "idle" });
  }, []);

  const update = useCallback((field, value) => {
    setForm((f) => ({
      ...f,
      [field]: typeof value === "function" ? value(f[field], f) : value,
    }));
    setErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors[field];

      if (field === "primaryContact") {
        Object.keys(nextErrors).forEach((k) => {
          if (k.startsWith("primaryContact")) delete nextErrors[k];
        });
      }
      if (field === "secondaryContacts") {
        Object.keys(nextErrors).forEach((k) => {
          if (/^secondaryContact\d+/.test(k)) delete nextErrors[k];
        });
        delete nextErrors.secondaryContactsRequired;
      }

      if (field === "websiteUrl" && typeof value !== "function") {
        const websiteValue = (value ?? "").toString().trim();
        if (websiteValue && !isValidWebsiteUrl(websiteValue)) {
          nextErrors.websiteUrl = "Use a valid format: https://google.com, https://www.google.com, or www.google.com";
        }
      }

      return nextErrors;
    });
  }, []);

  /* Clean up pending timers on unmount */
  useEffect(() => () => {
    clearTimeout(phoneTimerRef.current);
    clearTimeout(emailTimerRef.current);
  }, []);

  /**
   * Normalise a raw phone input value to a 12-digit 233XXXXXXXXX string
   * suitable for the uniqueness-check API. Returns null if the value is
   * not recognisably complete.
   */
  const toApiPhone = (raw) => {
    const digits = String(raw ?? "").replace(/\D/g, "");
    if (digits.length === 10 && digits.startsWith("0")) return `233${digits.slice(1)}`;
    if (digits.length === 12 && digits.startsWith("233"))  return digits;
    if (digits.length === 9)                                return `233${digits}`;
    return null;
  };

  const handlePhoneChange = useCallback((rawValue) => {
    update("corporatePhone", rawValue);
    setPhoneCheck({ status: "idle" });
    clearTimeout(phoneTimerRef.current);
    phoneRequestSeqRef.current += 1;
    const requestSeq = phoneRequestSeqRef.current;

    const normalized = toApiPhone(rawValue);
    if (!normalized) return;

    setPhoneCheck({ status: "checking" });
    phoneTimerRef.current = setTimeout(async () => {
      try {
        const exists = await uniquenessCheckApiService.checkPhone(normalized);
        if (requestSeq !== phoneRequestSeqRef.current) return;
        setPhoneCheck({ status: exists ? "taken" : "available" });
      } catch {
        if (requestSeq !== phoneRequestSeqRef.current) return;
        setPhoneCheck({ status: "error" });
      }
    }, 700);
  }, [update]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailChange = useCallback((rawValue) => {
    update("corporateEmail", rawValue);
    setEmailCheck({ status: "idle" });
    clearTimeout(emailTimerRef.current);
    emailRequestSeqRef.current += 1;
    const requestSeq = emailRequestSeqRef.current;

    const emailTrimmed = rawValue.trim();
    if (!emailTrimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) return;

    setEmailCheck({ status: "checking" });
    emailTimerRef.current = setTimeout(async () => {
      try {
        const exists = await uniquenessCheckApiService.checkEmail(emailTrimmed);
        if (requestSeq !== emailRequestSeqRef.current) return;
        setEmailCheck({ status: exists ? "taken" : "available" });
      } catch {
        if (requestSeq !== emailRequestSeqRef.current) return;
        setEmailCheck({ status: "error" });
      }
    }, 700);
  }, [update]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToResume = () => {
    setOverlayDir(1);
    setFlowChoice("resume");
  };
  const goBackToGate = () => {
    setOverlayDir(-1);
    setFlowChoice(null);
    setRecoveryCode("");
    setCodeLoadError("");
  };

  const useLocalDraft = () => {
    const draft = loadDraftFromStorage();
    if (draft) {
      setForm({
        ...draft.form,
        primaryCategories: normalizePrimaryCategoriesForUi(draft.form.primaryCategories),
      });
      setStep(draft.lastCompletedStep);
      setDraftCode(draft.draftCode || "");
      setDraftSlug(draft.draftSlug || "");
      setFlaggedContext(EMPTY_FLAGGED_CONTEXT);
      setFlowChoice("continue");
    } else {
      setOverlayDir(-1);
      setFlowChoice("no-draft");
    }
  };

  const hydrateDraftByCode = async (rawCode) => {
    const code = String(rawCode || "").trim().replace(/\s/g, "").toUpperCase();
    if (!code) {
      setCodeLoadError("Please enter your recovery code.");
      return;
    }
    setCodeLoadError("");
    setCodeLoading(true);
    try {
      const response = await partnerApiService.getDraftByCode(code);
      const envelope = extractResponseEnvelope(response);
      const draftRecord = resolveDraftRecord(envelope);
      const rawStatus = draftRecord?.status ?? draftRecord?.draft_status ?? draftRecord?.draftStatus ?? "";
      const statusNorm = rawStatus.toString().trim().toLowerCase();
      const approvalStatus = String(draftRecord?.approval_status ?? draftRecord?.approvalStatus ?? "").toLowerCase();
      const progressData = draftRecord?.progress_data || draftRecord?.progressData || {};
      const flaggedReason =
        progressData?.flag_reason ||
        progressData?.flag_reasons ||
        progressData?.progress_reason ||
        "";
      const isFlaggedRecord = Boolean(
        progressData?.is_flagged ||
        progressData?.flag_reason ||
        progressData?.flag_reasons ||
        (Array.isArray(progressData?.failed_documents) && progressData.failed_documents.length > 0),
      );
      const allowFlaggedResubmission =
        (approvalStatus === "in_progress" || approvalStatus === "flagged") &&
        isFlaggedRecord;

      // Backend can return draft as "draft" or the newer "draft_status".
      // Flagged in-progress records are intentionally allowed for correction.
      if (
        statusNorm &&
        statusNorm !== "draft" &&
        statusNorm !== "draft_status" &&
        statusNorm !== "re-submitted" &&
        statusNorm !== "resubmitted" &&
        statusNorm !== "re_submitted" &&
        !allowFlaggedResubmission
      ) {
        clearPartnerDraftStorage();
        setCodeLoadError(
          statusNorm === "submitted" || statusNorm === "completed"
            ? "This application has already been submitted."
            : "This record is not available to continue as a draft.",
        );
        return;
      }
      const rawDraftForm =
        resolveDraftForm(draftRecord) ||
        resolveDraftForm(envelope);
      const resolvedDraftCode = resolveDraftCode(envelope) || resolveDraftCode(draftRecord) || code;
      const resolvedDraftSlug =
        resolveDraftSlug(envelope) ||
        resolveDraftSlug(draftRecord) ||
        "";
      const draftForm = rawDraftForm && typeof rawDraftForm === "object"
        ? normalizeFormFromBackend(rawDraftForm)
        : INITIAL_FORM;
      let draftStep = Math.min(Math.max(1, resolveDraftStep(envelope) || resolveDraftStep(draftRecord)), STEPS.length);
      const normalizedDraftForm = {
        ...draftForm,
        primaryCategories: normalizePrimaryCategoriesForUi(draftForm.primaryCategories),
      };
      setForm(normalizedDraftForm);
      setDraftCode(resolvedDraftCode);
      setDraftSlug(resolvedDraftSlug);
      setRecoveryCode("");
      setFlowChoice("continue");
      if (allowFlaggedResubmission) {
        const rawDocumentStatuses = progressData?.documents || {};
        const normalizedDocumentStatuses = Object.fromEntries(
          Object.entries(rawDocumentStatuses).map(([docId, info]) => [
            docId,
            String(info?.status || info || "").trim().toLowerCase(),
          ]),
        );
        // Land the partner on the first incomplete / failed step when returning from email.
        const stepFlags = progressData?.steps || {};
        const firstFailedStep = [1, 2, 3, 4, 5, 6].find((id) => stepFlags[`step_${id}`] === false);
        const firstFailedDoc = Object.entries(normalizedDocumentStatuses).find(
          ([, status]) => status === "fail" || status === "failed" || status === "rejected",
        );
        if (firstFailedDoc) {
          // Docs live on verification (step 3) and commercial contract (step 5).
          draftStep = firstFailedDoc[0] === "contract_agreement" ? 5 : 3;
        } else if (firstFailedStep) {
          draftStep = firstFailedStep;
        }
        setFlaggedContext({
          active: true,
          flagReason: flaggedReason,
          progressSteps: stepFlags,
          documentStatuses: normalizedDocumentStatuses,
        });
      } else {
        setFlaggedContext(EMPTY_FLAGGED_CONTEXT);
      }
      setStep(draftStep);
      saveDraftToStorage(draftStep, normalizedDraftForm, {
        draftCode: resolvedDraftCode,
        draftSlug: resolvedDraftSlug,
      });
    } catch (error) {
      if (error?.response?.status === 404) {
        setCodeLoadError("Invalid or expired code. Please check and try again.");
      } else {
        setCodeLoadError("Could not load draft. Please check your connection and try again.");
      }
    } finally {
      setCodeLoading(false);
    }
  };

  const loadDraftByCode = () => hydrateDraftByCode(recoveryCode);

  const chooseStartFresh = () => {
    clearPartnerDraftStorage();
    setForm(INITIAL_FORM);
    setStep(1);
    setErrors({});
    setDraftCode("");
    setDraftSlug("");
    setFlaggedContext(EMPTY_FLAGGED_CONTEXT);
    setFlowChoice("fresh");
    setPhoneCheck({ status: "idle" });
    setEmailCheck({ status: "idle" });
    clearTimeout(phoneTimerRef.current);
    clearTimeout(emailTimerRef.current);
  };

  useEffect(() => {
    const urlCode = String(draftCodeFromUrl || "").trim();
    if (!urlCode) return;
    setFlowChoice("resume");
    setRecoveryCode(urlCode.toUpperCase());
    hydrateDraftByCode(urlCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftCodeFromUrl]);

  const persistDraftQuietly = useCallback(async (currentForm, currentStep) => {
    try {
      const payload = buildPartnerPayload(currentForm, {
        draft_status: "draft",
        lastCompletedStep: currentStep,
        draftCode: draftCode || undefined,
        draft_code: draftCode || undefined,
        ...(draftSlug ? { slug: draftSlug } : {}),
      });
      const response = await partnerApiService.initiateDraft(payload);
      const envelope = extractResponseEnvelope(response);
      const resolvedCode = resolveDraftCode(envelope) || draftCode;
      let resolvedSlug = resolveDraftSlug(envelope) || draftSlug;
      if (resolvedCode && !resolvedSlug) {
        try {
          const draftResponse = await partnerApiService.getDraftByCode(resolvedCode);
          const draftEnvelope = extractResponseEnvelope(draftResponse);
          const draftRecord = resolveDraftRecord(draftEnvelope);
          resolvedSlug =
            resolveDraftSlug(draftEnvelope) ||
            resolveDraftSlug(draftRecord) ||
            resolvedSlug;
        } catch {
          /* ignore */
        }
      }
      if (resolvedCode) setDraftCode(resolvedCode);
      if (resolvedSlug) setDraftSlug(resolvedSlug);
      saveDraftToStorage(currentStep, currentForm, {
        draftCode: resolvedCode,
        draftSlug: resolvedSlug,
      });
      return { code: resolvedCode, slug: resolvedSlug, envelope };
    } catch {
      return null;
    }
  }, [draftCode, draftSlug]);

  /** Persists current form; only step 1 (company basics) must be valid — later steps e.g. documents may be incomplete. */
  const saveDraft = async () => {
    const errs = validateCompanyInfo(form);
    // Block save if uniqueness checks are conclusively bad or still in-flight
    if (phoneCheck.status === "taken")
      errs.corporatePhone = "This phone number is already registered on InfoBoxx";
    else if (phoneCheck.status === "checking")
      errs.corporatePhone = "Please wait — verifying phone number…";
    if (emailCheck.status === "taken")
      errs.corporateEmail = "This email address is already registered on InfoBoxx";
    else if (emailCheck.status === "checking")
      errs.corporateEmail = "Please wait — verifying email address…";
    if (Object.keys(errs).length) {
      setErrors(errs);
      setStep(1);
      setDir(-1);
      return;
    }

    setIsSavingDraft(true);
    try {
      const payload = buildPartnerPayload(form, {
        draft_status: "draft",
        lastCompletedStep: step,
        draftCode: draftCode || undefined,
        draft_code: draftCode || undefined,
        // Keep the server UUID once we have it — do not re-slugify the company name.
        ...(draftSlug ? { slug: draftSlug } : {}),
      });
      const response = await partnerApiService.initiateDraft(payload);
      const envelope = extractResponseEnvelope(response);
      let resolvedCode = resolveDraftCode(envelope) || draftCode;
      let resolvedSlug = resolveDraftSlug(envelope) || draftSlug;

      // Initiate may only return draft_code; pull the UUID slug from get_draft when needed.
      if (resolvedCode && !resolvedSlug) {
        try {
          const draftResponse = await partnerApiService.getDraftByCode(resolvedCode);
          const draftEnvelope = extractResponseEnvelope(draftResponse);
          const draftRecord = resolveDraftRecord(draftEnvelope);
          resolvedSlug =
            resolveDraftSlug(draftEnvelope) ||
            resolveDraftSlug(draftRecord) ||
            resolvedSlug;
          if (!resolvedCode) {
            resolvedCode = resolveDraftCode(draftEnvelope) || resolvedCode;
          }
        } catch {
          /* keep name-based slug only if get_draft is unavailable */
        }
      }

      if (resolvedCode) setDraftCode(resolvedCode);
      if (resolvedSlug) setDraftSlug(resolvedSlug);
      saveDraftToStorage(step, form, {
        draftCode: resolvedCode,
        draftSlug: resolvedSlug,
      });
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
      setOverlayDir(-1);
      setFlowChoice(null);
      setActionModal({
        open: true,
        status: "success",
        title: "Draft saved!",
        message: "Your progress has been saved. Use the recovery code below to continue your application at any time.",
        details: resolvedCode ? "Your recovery code" : "",
        code: resolvedCode || "",
        steps: [],
      });
    } catch (error) {
      setActionModal({
        open: true,
        status: "error",
        title: "Could not save draft",
        message: "Something went wrong while saving your draft. Please try again.",
        details: error?.response?.data?.message || "",
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const goTo = (target) => {
    const inRange = target >= 1 && target <= STEPS.length;
    if (!inRange) return;
    if (!isFlaggedResubmissionFlow && target >= step) return;
    setErrors({});
    setDir(target > step ? 1 : -1);
    setStep(target);
  };

  const next = () => {
    if (step >= 1 && step <= STEPS.length) {
      const stepErrs = validateStep(step, form);
      if (step === 1 || step === 2) {
        // Block on uniqueness results that are conclusively bad or still pending
        if (phoneCheck.status === "taken")
          stepErrs.corporatePhone = "This phone number is already registered on InfoBoxx";
        else if (phoneCheck.status === "checking")
          stepErrs.corporatePhone = "Please wait — verifying phone number…";
        if (emailCheck.status === "taken")
          stepErrs.corporateEmail = "This email address is already registered on InfoBoxx";
        else if (emailCheck.status === "checking")
          stepErrs.corporateEmail = "Please wait — verifying email address…";
      }
      if (Object.keys(stepErrs).length) {
        setErrors((prev) => ({ ...prev, ...stepErrs }));
        scrollToFirstMissingField(step, stepErrs);
        return;
      }
    }
    if (step < STEPS.length) {
      const stepStarted = getStepStarted(step, form);
      if (stepStarted) {
        const stepErrs = validateStep(step, form);
        if (Object.keys(stepErrs).length) {
          setErrors((prev) => ({ ...prev, ...stepErrs }));
          scrollToFirstMissingField(step, stepErrs);
          return;
        }
      }
    }
    setErrors({});
    setDir(1);
    const nextStep = Math.min(step + 1, STEPS.length);
    setStep(nextStep);
    // Persist each completed step so the DB never stays stuck on early company-only data.
    void persistDraftQuietly(form, step);
  };

  const prev = () => {
    setErrors({});
    setDir(-1);
    setStep((s) => Math.max(s - 1, 1));
  };

  const buildFinalSubmissionPayload = (sourceForm = form) => buildPartnerPayload(sourceForm, {
    draft_status: isFlaggedResubmissionFlow ? "re-submitted" : "submitted",
    draftCode: draftCode || undefined,
    draft_code: draftCode || undefined,
    ...(draftSlug ? { slug: draftSlug } : {}),
  });

  const showFinalPayload = () => {
    const payload = buildFinalSubmissionPayload();
    setPayloadPreview({ open: true, text: JSON.stringify(payload, null, 2) });
  };

  const submit = async () => {
    const { allErrors, firstInvalidStep } = validateFullForm(form);
    if (Object.keys(allErrors).length) {
      setErrors(allErrors);
      if (firstInvalidStep != null) setStep(firstInvalidStep);
      setDir(-1);
      return;
    }

    setIsSubmitting(true);
    try {
      // Phase 1: persist full form via draft/initiate so backend can store text fields
      // and convert base64 uploads to storage URLs (avoids oversized final payloads).
      let workingForm = form;
      const draftPersist = await persistDraftQuietly(form, STEPS.length);
      if (draftPersist?.code) {
        try {
          const draftResponse = await partnerApiService.getDraftByCode(draftPersist.code);
          const draftEnvelope = extractResponseEnvelope(draftResponse);
          const draftRecord = resolveDraftRecord(draftEnvelope);
          const serverForm =
            resolveDraftForm(draftRecord) ||
            resolveDraftForm(draftEnvelope);
          workingForm = mergeServerUploadsIntoForm(form, serverForm);
          setForm(workingForm);
        } catch {
          /* continue with local form */
        }
      }

      // Phase 2: mark submitted with URL-first documents where possible.
      const payload = buildFinalSubmissionPayload(workingForm);
      await partnerApiService.saveFullRegistration(payload);
      clearPartnerDraftStorage();
      setForm(INITIAL_FORM);
      setStep(1);
      setDir(1);
      setErrors({});
      setDraftCode("");
      setDraftSlug("");
      setFlaggedContext(EMPTY_FLAGGED_CONTEXT);
      setOverlayDir(-1);
      setFlowChoice(null);

      if (isFlaggedResubmissionFlow) {
        // Strip the draft code from the URL and show the dedicated re-submission success page.
        navigate("/partners/onboard", { replace: true });
        setResubmitDone(true);
      } else {
        setActionModal({
          open: true,
          status: "success",
          title: "Application Submitted!",
          message:
            "Thank you for applying to the InfoBoxx Partner Network. Our team will review your application within 2–5 business days" +
            (form.corporateEmail ? ` and contact you at ${form.corporateEmail}.` : "."),
          details: "",
          steps: [
            { icon: CheckCircle2, text: "Application received and securely timestamped",   accent: "#FFD221" },
            { icon: ShieldCheck,  text: "Compliance review underway (2–5 business days)",  accent: "#FFD221" },
            { icon: KeyRound,     text: "Dashboard credentials sent to your email on approval", accent: "#E5B80B" },
            { icon: Sparkles,     text: "Log in, publish your products, and go live",      accent: "#E5B80B" },
          ],
          purgeLocalDraftOnDismiss: true,
        });
      }
    } catch (error) {
      setActionModal({
        open: true,
        status: "error",
        title: "Submission failed",
        message: "We could not submit your full onboarding application at this time.",
        details: error?.response?.data?.message || "Please retry shortly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const completion = computeCompletion(form);
  const progress = completion.percent;
  const currentStep = STEPS[step - 1];
  const nextStep = STEPS[step] || null;
  const currentStepLocked =
    isFlaggedResubmissionFlow &&
    step !== 3 &&
    Boolean(flaggedContext.progressSteps?.[`step_${step}`]);
  const flaggedEditableDocIds = useMemo(() => {
    if (!isFlaggedResubmissionFlow) return null;
    const statuses = flaggedContext.documentStatuses || {};
    return REQUIRED_DOCS
      .map((doc) => doc.id)
      .filter((id) => {
        const status = String(statuses[id] || "").toLowerCase();
        return !status || status === "fail" || status === "failed" || status === "missing" || status === "pending";
      });
  }, [flaggedContext.documentStatuses, isFlaggedResubmissionFlow]);
  const stepStarted = useMemo(() => getStepStarted(step, form), [step, form]);
  const currentStepErrors = useMemo(() => validateStep(step, form), [step, form]);
  const currentStepIncompleteCount = Object.keys(currentStepErrors).length;
  const shouldWarnStepCompletion = step < STEPS.length && stepStarted && currentStepIncompleteCount > 0;

  /* Strict flagged-flow gate: unresolved corrections keep re-submit disabled. */
  const flaggedCorrectionBlockers = useMemo(() => {
    if (!isFlaggedResubmissionFlow) return [];

    const blockers = [];

    STEPS.forEach((s) => {
      const status = stepFlaggedStatus[s.id]?.type;
      if (status === "needs_review") {
        const errs = validateStep(s.id, form);
        if (Object.keys(errs).length > 0) {
          blockers.push({ key: `step-${s.id}`, label: s.label, type: "step" });
        }
      }
    });

    const statuses = flaggedContext.documentStatuses || {};
    REQUIRED_DOCS.filter((doc) => doc.id !== "contract_agreement").forEach((doc) => {
      const status = String(statuses[doc.id] || "").toLowerCase();
      const needsReplacement = !status || status === "fail" || status === "failed" || status === "missing" || status === "pending";
      if (!needsReplacement) return;

      const uploadedDoc = form.documents?.[doc.id];
      const hasFreshUpload = isBase64Entry(uploadedDoc);
      if (!hasFreshUpload) {
        blockers.push({ key: `doc-${doc.id}`, label: doc.label, type: "document" });
      }
    });

    return blockers;
  }, [isFlaggedResubmissionFlow, stepFlaggedStatus, form, flaggedContext.documentStatuses]);

  const canSubmitFinal = isFullFormComplete && (!isFlaggedResubmissionFlow || flaggedCorrectionBlockers.length === 0);
  const submitDisabledReason = !isFullFormComplete
    ? "Complete all required fields to submit"
    : isFlaggedResubmissionFlow && flaggedCorrectionBlockers.length > 0
      ? "Finish all flagged corrections (including required document replacements) to re-submit"
      : undefined;

  const clearCurrentStep = () => {
    setForm((f) => {
      if (step === 1) {
        return {
          ...f,
          legalName: "",
          tradingName: "",
          companyType: "",
          companyTypeCustom: "",
          yearIncorporated: "",
          businessDescription: "",
          registeredAddress: "",
          city: "",
          region: "",
          gpsLocation: "",
          websiteUrl: "",
          logo: null,
        };
      }
      if (step === 2) {
        return {
          ...f,
          corporatePhone: "",
          corporateEmail: "",
          primaryContact: { ...f.primaryContact, fullName: "", jobTitle: "", email: "", phone: "", alternatePhone: "" },
        };
      }
      if (step === 3) {
        const nextDocuments = { ...f.documents };
        delete nextDocuments.cert_incorporation;
        delete nextDocuments.cert_commence;
        return {
          ...f,
          registrationNumber: "",
          tin: "",
          documents: nextDocuments,
          primaryContact: { ...f.primaryContact, idType: "", idNumber: "", idFront: null, idBack: null },
        };
      }
      if (step === 4) {
        return {
          ...f,
          primaryCategories: [],
          subCategories: [],
          specificServices: [],
          coverageAreas: [],
          yearsOfExperience: "",
          numberOfStaff: "",
          serviceMode: "",
          languagesSpoken: [],
          willProvideContent: "",
          contentTypes: [],
          hasExistingContent: "",
          contentUploadCapability: "",
          preferredContactMethod: "",
          acceptsInstantLeads: "",
          responseTimeSla: "",
          appointmentAvailability: "",
        };
      }
      if (step === 5) {
        return {
          ...f,
          commercialInterests: [],
          budgetRange: "",
          paymentMethod: "",
        };
      }
      if (step === 6) {
        return {
          ...f,
          declarationAccuracy: false,
          consentDataProcessing: false,
          acceptTerms: false,
          digitalSignatureName: "",
        };
      }
      return f;
    });
    setErrors((prev) => {
      const nextErrors = { ...prev };
      Object.keys(nextErrors).forEach((key) => {
        if (key in currentStepErrors) delete nextErrors[key];
      });
      return nextErrors;
    });
  };
  const StepComponent = STEP_COMPONENTS[step - 1];

  /* ── RE-SUBMISSION SUCCESS: full page, shown after flagged corrections are re-submitted ── */
  if (resubmitDone) {
    return (
      <ResubmissionSuccessPage
        companyName={form?.tradingName || form?.legalName || ""}
        email={form?.corporateEmail || ""}
      />
    );
  }

  /* ── OVERLAY SCREENS: gate, resume, no-draft — single wrapper with AnimatePresence ── */
  if (flowChoice === null || flowChoice === "resume" || flowChoice === "no-draft") {
    return (
      <div className="min-h-screen bg-[#f7f4ec] pt-16 relative">
        <div className="fixed inset-0 z-10 flex flex-col items-center justify-start overflow-y-auto px-4 pb-20 pt-28 sm:pt-32 md:pt-36 bg-gradient-to-b from-white via-[#fffdf6] to-[#f7f4ec]">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,210,33,0.2),transparent_42%),radial-gradient(ellipse_at_100%_80%,rgba(255,210,33,0.1),transparent_45%)]" aria-hidden />
          <AnimatePresence mode="wait" custom={overlayDir}>
            {flowChoice === null && (
              <motion.div
                key="gate"
                custom={overlayDir}
                variants={overlayCardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="flex relative z-20 flex-col items-center w-full max-w-lg"
              >
                <div className="w-full overflow-hidden rounded-3xl border border-[#FFD221]/25 bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)]">
            <div className="h-1.5 bg-gradient-to-r from-[#FFD221] via-[#c9a000] to-black" />
            <div className="p-8 sm:p-10">
              <div className="mb-4 flex justify-center">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#FFD221]/40 bg-[#FFD221]/15 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-[#9a7b00]">
                  <span className="text-[#FFD221] font-black">Info</span><span className="font-black text-black">Boxx</span>
                  <span className="mx-1 text-[#FFD221]/50">·</span>
                  Partner Onboarding
                </span>
              </div>
              <h1 className="mb-2 text-xl font-black text-center text-gray-900 sm:text-2xl">
                Welcome to partner onboarding
              </h1>
              <p className="mb-8 text-sm text-center text-gray-500">
                Are you continuing a previous application or starting fresh?
              </p>
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={goToResume}
                  className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-[#FFD221]/40 bg-[#FFD221]/[0.06] hover:border-[#FFD221]/70 hover:bg-[#FFD221]/[0.12] transition-all text-left group"
                >
                  <span className="w-12 h-12 rounded-xl bg-[#FFD221]/20 flex items-center justify-center shrink-0 group-hover:bg-[#FFD221]/35 transition-colors">
                    <FileText size={22} className="text-[#9a7b00]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">Continue previous application</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Use a draft on this device or enter a recovery code
                    </p>
                  </div>
                  <ArrowRight size={18} className="text-gray-400 shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={chooseStartFresh}
                  className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-gray-200 bg-white hover:border-[#FFD221]/50 hover:bg-[#fffdf4] transition-all text-left group"
                >
                  <span className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center shrink-0 group-hover:bg-[#FFD221]/15 transition-colors">
                    <RotateCcw size={22} className="text-gray-500 group-hover:text-[#9a7b00]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900">Start fresh</p>
                    <p className="text-xs text-gray-500 mt-0.5">Begin a new partner application</p>
                  </div>
                  <ArrowRight size={18} className="text-gray-400 shrink-0" />
                </button>
              </div>
            </div>
                </div>
                <p className="mt-6 text-center">
                  <Link to="/partners" className="text-sm font-semibold text-gray-600 hover:text-[#9a7b00] transition-colors inline-flex items-center gap-1.5">
                    <ArrowLeft size={14} />
                    Back to Partners
                  </Link>
                </p>
              </motion.div>
            )}

            {flowChoice === "resume" && (
              <motion.div
                key="resume"
                custom={overlayDir}
                variants={overlayCardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="flex relative z-20 flex-col items-center w-full max-w-lg"
              >
                <div className="w-full overflow-hidden rounded-3xl border border-[#FFD221]/25 bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)]">
            <div className="h-1.5 bg-gradient-to-r from-[#FFD221] via-[#c9a000] to-black" />
            <div className="p-8 sm:p-10">
              <h1 className="mb-2 text-xl font-black text-center text-gray-900 sm:text-2xl">
                Continue your application
              </h1>
              <p className="mb-8 text-sm text-center text-gray-500">
                Use a draft saved on this device or enter the recovery code you received when you saved.
              </p>

              {hasDraftInStorage() && (
                <div className="mb-6">
                  <button
                    type="button"
                    onClick={useLocalDraft}
                    className="w-full flex items-center gap-4 p-5 rounded-2xl border-2 border-[#FFD221]/40 bg-[#FFD221]/[0.06] hover:border-[#FFD221]/70 hover:bg-[#FFD221]/[0.12] transition-all text-left group"
                  >
                    <span className="w-12 h-12 rounded-xl bg-[#FFD221]/20 flex items-center justify-center shrink-0 group-hover:bg-[#FFD221]/35 transition-colors">
                      <FileText size={22} className="text-[#9a7b00]" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900">Continue with draft on this device</p>
                      <p className="text-xs text-gray-500 mt-0.5">We found a saved draft</p>
                    </div>
                    <ArrowRight size={18} className="text-gray-400 shrink-0" />
                  </button>
                </div>
              )}

              {hasDraftInStorage() && (
                <p className="mb-4 text-xs text-center text-gray-400">Or</p>
              )}

              <div>
                <label className="block mb-2 text-xs font-semibold tracking-wide text-gray-600 uppercase">
                  Enter recovery code
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Use the code you received when you saved your draft (e.g. from email or when you clicked “Save draft”).
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={recoveryCode}
                    onChange={(e) => {
                      setRecoveryCode(e.target.value);
                      setCodeLoadError("");
                    }}
                    placeholder="e.g. A1B2-C3D4"
                    className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm placeholder:text-gray-300 focus:ring-2 focus:ring-[#FFD221]/50 focus:border-[#FFD221] outline-none transition-all"
                    disabled={codeLoading}
                  />
                  <button
                    type="button"
                    onClick={loadDraftByCode}
                    disabled={codeLoading}
                    className="shrink-0 px-5 py-3 rounded-xl text-sm font-bold text-black bg-[#FFD221] hover:bg-[#c9a000] disabled:opacity-60 transition-all"
                  >
                    {codeLoading ? "Loading…" : "Load draft"}
                  </button>
                </div>
                {codeLoadError && (
                  <p className="flex gap-1 items-center mt-2 text-xs text-rose-500">
                    <AlertCircle size={12} />
                    {codeLoadError}
                  </p>
                )}
              </div>
            </div>
                </div>
                <p className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={goBackToGate}
                    className="text-sm font-semibold text-gray-600 hover:text-[#9a7b00] transition-colors inline-flex items-center gap-1.5"
                  >
                    <ArrowLeft size={14} />
                    Back to choices
                  </button>
                </p>
              </motion.div>
            )}

            {flowChoice === "no-draft" && (
              <motion.div
                key="no-draft"
                custom={overlayDir}
                variants={overlayCardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="flex relative z-20 flex-col items-center w-full max-w-md"
              >
                <div className="w-full overflow-hidden rounded-3xl border border-[#FFD221]/25 bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)]">
            <div className="h-1.5 bg-gradient-to-r from-[#FFD221] via-[#c9a000] to-black" />
            <div className="p-8 text-center sm:p-10">
              <div className="flex justify-center items-center mx-auto mb-5 w-14 h-14 bg-amber-100 rounded-2xl">
                <AlertCircle size={26} className="text-amber-600" />
              </div>
              <h2 className="mb-2 text-lg font-black text-gray-900">No saved draft found</h2>
              <p className="mb-8 text-sm text-gray-500">
                We couldn’t find a previous application on this device. You can start a new application below.
              </p>
              <button
                type="button"
                onClick={chooseStartFresh}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-bold text-black bg-[#FFD221] hover:bg-[#c9a000] transition-all shadow-lg shadow-[#FFD221]/30"
              >
                <RotateCcw size={16} />
                Start fresh application
              </button>
            </div>
                </div>
                <p className="mt-6 text-center">
                  <Link to="/partners" className="text-sm font-semibold text-gray-600 hover:text-[#9a7b00] transition-colors inline-flex items-center gap-1.5">
                    <ArrowLeft size={14} />
                    Back to Partners
                  </Link>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <ActionStatusModal
          open={actionModal.open}
          status={actionModal.status}
          title={actionModal.title}
          message={actionModal.message}
          details={actionModal.details}
          code={actionModal.code}
          steps={actionModal.steps}
          onClose={closeActionModal}
        />
      </div>
    );
  }

  /* ── MAIN FORM LAYOUT ── */
  return (
    <div className="min-h-screen bg-[#f7f4ec] pt-16">
      {/* ── TOP PROGRESS BAR (thin, full-width) ── */}
      <div className="fixed top-16 left-0 right-0 z-50 h-0.5 bg-black/10">
        <motion.div
          className="h-full bg-gradient-to-r from-[#FFD221] to-[#c9a000]"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      </div>

      <div className="px-4 py-8 mx-auto max-w-8xl sm:px-6 lg:px-8 lg:py-10">
        {/* ── BACK + SAVE ROW ── */}
        <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2 items-center">
            <Link to="/partners"
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-gray-600 transition-all hover:border-[#FFD221]/60 hover:bg-[#FFD221]/10 hover:text-black">
              <ArrowLeft size={15} />
              Back to Partners
            </Link>
            <button
              type="button"
              onClick={goBackToGate}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-gray-600 transition-all hover:border-[#FFD221]/60 hover:bg-[#FFD221]/10 hover:text-black"
            >
              <RotateCcw size={13} />
              Start fresh / Recovery code
            </button>
            {SHOW_FILL_TEST_DATA_BUTTON && (
              <button
                type="button"
                onClick={applyTestPartnerFill}
                title="Fills text/select fields. Upload logo, ID photos, and documents yourself."
                className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-amber-400 bg-amber-50/90 px-3 py-2 text-xs font-bold text-amber-950 hover:bg-amber-100 transition-colors"
              >
                <Wand2 size={14} className="shrink-0" />
                Fill test data
              </button>
            )}
          </div>
          {isFullFormComplete ? (
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-[#9a7b00] bg-[#FFD221]/15 border border-[#FFD221]/40">
              <CheckCircle2 size={11} />
              Ready to submit
            </span>
          ) : isStep1Complete ? (
            <button type="button" onClick={saveDraft} disabled={isSavingDraft}
              className={`hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all duration-300 border ${
                draftSaved
                  ? "border-[#FFD221]/50 bg-[#FFD221]/15 text-[#9a7b00]"
                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
              } disabled:opacity-60 disabled:cursor-not-allowed`}>
              {draftSaved ? <CheckCircle2 size={13} /> : <Save size={13} />}
              {isSavingDraft ? "Saving…" : draftSaved ? "Draft Saved!" : "Save Draft"}
            </button>
          ) : (
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200">
              <AlertCircle size={11} />
              Complete Step 1 to save draft
            </span>
          )}
        </div>

        {isFlaggedResubmissionFlow && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="overflow-hidden mb-6 rounded-2xl border shadow-sm border-amber-300/70 shadow-amber-100"
          >
            {/* ── Collapsed strip (always visible) ── */}
            <button
              type="button"
              onClick={() => setFlagReasonExpanded((p) => !p)}
              className="flex gap-3 items-center px-4 py-3 w-full text-left bg-gradient-to-r from-amber-50 transition-all to-amber-100/60 hover:from-amber-100 hover:to-amber-50"
            >
              <span className="flex justify-center items-center w-7 h-7 text-amber-600 rounded-lg shrink-0 bg-amber-500/15">
                <Flag size={13} />
              </span>
              <div className="flex-1 min-w-0">
                <span className="text-[11px] font-black uppercase tracking-wider text-amber-700">Flagged application — action required</span>
                {!flagReasonExpanded && flagReasonChips.length > 0 && (
                  <p className="mt-0.5 text-xs text-amber-800 truncate">
                    {flagReasonChips[0]}
                    {flagReasonChips.length > 1 && (
                      <span className="ml-1.5 inline-flex items-center rounded-full bg-amber-200/70 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                        +{flagReasonChips.length - 1} more
                      </span>
                    )}
                  </p>
                )}
              </div>
              <div className="flex gap-2 items-center shrink-0">
                <Link
                  to="/contact"
                  onClick={(e) => e.stopPropagation()}
                  className="hidden sm:inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-[11px] font-bold text-amber-800 transition-all hover:bg-amber-50"
                >
                  Contact us
                </Link>
                <span className="flex justify-center items-center w-6 h-6 text-amber-700 rounded-lg bg-amber-200/60">
                  {flagReasonExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </span>
              </div>
            </button>

            {/* ── Expanded details panel ── */}
            <AnimatePresence>
              {flagReasonExpanded && (
                <motion.div
                  key="flag-details"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
                  style={{ overflow: "hidden" }}
                >
                  <div className="px-4 py-4 bg-white border-t border-amber-200">
                    <p className="mb-3 text-xs font-semibold text-gray-600">
                      Your application was flagged for the following reason(s). Update only the highlighted sections, then re-submit.
                    </p>
                    {flagReasonChips.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {flagReasonChips.map((chip, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900"
                          >
                            <AlertTriangle size={11} className="text-amber-500 shrink-0" />
                            {chip}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mb-3 text-xs text-amber-800">{flaggedContext.flagReason}</p>
                    )}
                    <div className="flex gap-3 justify-between items-center pt-3 border-t border-amber-100">
                      <p className="text-[11px] text-gray-500">Need help understanding the flag? Our team is ready to assist.</p>
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800 transition-all hover:bg-amber-100 whitespace-nowrap"
                      >
                        Contact us
                      </Link>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* ── MAIN GRID: Sidebar + Form ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] xl:grid-cols-[320px_1fr] gap-6">

          {/* ════════════════════════════════════
              LEFT SIDEBAR
          ════════════════════════════════════ */}
          <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            {/* Branding + progress — light InfoBoxx panel */}
            <div className="relative overflow-hidden rounded-2xl border border-[#FFD221]/35 bg-gradient-to-br from-white to-[#fffdf4] p-5 shadow-sm">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_100%_0%,rgba(255,210,33,0.18),transparent_55%)]" aria-hidden />
              <div className="relative z-10">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-lg font-black text-[#FFD221]">Info</span>
                  <span className="text-lg font-black text-gray-900">Boxx</span>
                </div>
                <p className="text-xs font-semibold text-gray-500">Partner Onboarding Application</p>

                <div className="mt-4 flex items-center gap-3">
                  <div className="relative h-12 w-12 shrink-0">
                    <svg className="h-12 w-12 -rotate-90" viewBox="0 0 48 48">
                      <circle cx="24" cy="24" r="20" fill="none" stroke="#f3e8c8" strokeWidth="4" />
                      <motion.circle cx="24" cy="24" r="20" fill="none" stroke="#FFD221" strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 20}`}
                        animate={{ strokeDashoffset: 2 * Math.PI * 20 * (1 - progress / 100) }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                      />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-gray-900">
                      {Math.round(progress)}%
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-900">{Math.round(progress)}% Complete</p>
                    <p className="mt-0.5 text-[11px] text-gray-500">Step {step} of {STEPS.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step list — cream / yellow InfoBoxx stepper */}
            <div className="overflow-hidden rounded-2xl border border-[#FFD221]/25 bg-gradient-to-b from-white to-[#fffdf4] shadow-sm">
              {STEPS.map((s) => {
                const Icon = s.icon;
                const done = step > s.id;
                const active = step === s.id;
                const locked = !isFlaggedResubmissionFlow && step < s.id;
                const flagStatus = stepFlaggedStatus[s.id];
                const isStepApproved = isFlaggedResubmissionFlow && flagStatus?.type === "approved";
                const isStepNeedsAction = isFlaggedResubmissionFlow && (flagStatus?.type === "needs_action" || flagStatus?.type === "needs_review");
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={locked}
                    onClick={() => goTo(s.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-all duration-200 border-b border-[#FFD221]/10 last:border-b-0
                      ${active
                        ? isStepApproved
                          ? "bg-emerald-50/70 border-l-2 border-l-emerald-400"
                          : isStepNeedsAction
                            ? "bg-amber-50/60 border-l-2 border-l-amber-400"
                            : "bg-[#FFF9E6] border-l-[3px] border-l-[#FFD221]"
                        : done
                          ? isStepApproved
                            ? "hover:bg-emerald-50/40 cursor-pointer border-l-2 border-l-emerald-300"
                            : isStepNeedsAction
                              ? "hover:bg-amber-50/40 cursor-pointer border-l-2 border-l-amber-300"
                              : "cursor-pointer hover:bg-[#FFF9E6]/80"
                          : "cursor-default bg-[#fffdf8]/90"
                      }`}
                  >
                    {/* Step circle */}
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all ${
                      active
                        ? isStepApproved
                          ? "bg-emerald-500 shadow-md shadow-emerald-200"
                          : isStepNeedsAction
                            ? "bg-amber-500 shadow-md shadow-amber-200"
                            : "bg-[#FFD221] shadow-md shadow-[#FFD221]/40"
                        : isStepApproved
                          ? "bg-emerald-100"
                          : isStepNeedsAction && done
                            ? "bg-amber-100"
                            : done
                              ? "bg-[#FFD221]/25"
                              : "border border-[#FFD221]/30 bg-white text-[#9a7b00]"
                    }`}>
                      {isStepApproved
                        ? <Lock size={11} className={active ? "text-white" : "text-emerald-600"} strokeWidth={3} />
                        : done
                          ? <Check size={11} className={isStepNeedsAction ? (active ? "text-white" : "text-amber-600") : "text-[#9a7b00]"} strokeWidth={3} />
                          : active
                            ? <Icon size={13} className="text-black" />
                            : <span className="text-[10px] font-bold text-[#9a7b00]">{s.id}</span>
                      }
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold truncate ${
                        active
                          ? isStepApproved ? "text-emerald-800" : isStepNeedsAction ? "text-amber-900" : "text-black"
                          : done
                            ? isStepApproved ? "text-emerald-700" : isStepNeedsAction ? "text-amber-800" : "text-gray-700"
                            : "text-gray-600"
                      }`}>
                        {s.label}
                      </p>
                      {/* Flagged flow micro status line */}
                      {isFlaggedResubmissionFlow && (done || active) && (
                        <p className={`text-[10px] font-semibold mt-0.5 ${
                          isStepApproved
                            ? "text-emerald-600"
                            : isStepNeedsAction
                              ? "text-amber-600"
                              : "text-gray-400"
                        }`}>
                          {isStepApproved
                            ? "Approved · read-only"
                            : flagStatus?.count
                              ? `${flagStatus.count} doc${flagStatus.count > 1 ? "s" : ""} need re-upload`
                              : "Review needed"}
                        </p>
                      )}
                      {!isFlaggedResubmissionFlow && active && (
                        <p className="text-[10px] text-[#9a7b00]/80 truncate mt-0.5">{s.subtitle}</p>
                      )}
                    </div>

                    {/* Right indicator */}
                    {isFlaggedResubmissionFlow && (done || active) ? (
                      isStepApproved ? (
                        <span className="flex justify-center items-center ml-auto w-5 h-5 text-emerald-600 bg-emerald-100 rounded-full shrink-0">
                          <Check size={10} strokeWidth={3} />
                        </span>
                      ) : flagStatus?.count ? (
                        <span className="ml-auto shrink-0 inline-flex items-center justify-center rounded-full bg-amber-100 border border-amber-200 px-1.5 py-0.5 text-[10px] font-black text-amber-700 min-w-[20px]">
                          {flagStatus.count}
                        </span>
                      ) : (
                        <span className="flex justify-center items-center ml-auto w-5 h-5 text-amber-600 bg-amber-100 rounded-full shrink-0">
                          <AlertTriangle size={10} />
                        </span>
                      )
                    ) : done && !active ? (
                      <CheckCircle2 size={13} className="text-[#9a7b00] shrink-0" />
                    ) : active ? (
                      <motion.div className="w-1 h-5 rounded-full bg-[#FFD221] shrink-0"
                        layoutId="activePip"
                        transition={{ type: "spring", stiffness: 300, damping: 28 }}
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>

            {/* Context tip */}
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="flex gap-3 rounded-2xl border border-[#FFD221]/30 bg-[#FFF9E6]/80 p-4"
              >
                <Info size={14} className="mt-0.5 shrink-0 text-[#9a7b00]" />
                <p className="text-xs leading-relaxed text-gray-600">{currentStep.tip}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* ════════════════════════════════════
              RIGHT — FORM CARD
          ════════════════════════════════════ */}
          <div className="flex flex-col">
            <div className="flex overflow-hidden flex-col bg-white rounded-2xl border border-gray-100 shadow-sm">

              {/* Form header — transitions with step */}
              <div className="relative px-7 sm:px-8 pt-7 pb-6 border-b border-gray-100 overflow-hidden bg-gradient-to-r from-white to-[#fffdf4]">
                <div className="absolute top-0 right-0 w-56 h-56 rounded-full bg-[#FFD221]/[0.08] blur-3xl pointer-events-none -translate-y-1/3 translate-x-1/4" />
                <AnimatePresence mode="wait" custom={dir}>
                  <motion.div
                    key={step}
                    custom={dir}
                    variants={pageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="flex relative z-10 gap-4 justify-between items-start"
                  >
                    <div>
                      <div className="flex flex-wrap gap-2 items-center mb-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFD221]/20 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-[#9a7b00]">
                          Step {step} / {STEPS.length}
                        </span>
                        {isFlaggedResubmissionFlow && stepFlaggedStatus[step] && (
                          stepFlaggedStatus[step].type === "approved" ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-[10px] font-black text-emerald-700 uppercase tracking-wider">
                              <Lock size={9} />
                              Approved · read-only
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-200 text-[10px] font-black text-amber-700 uppercase tracking-wider">
                              <AlertTriangle size={9} />
                              {stepFlaggedStatus[step]?.count
                                ? `${stepFlaggedStatus[step].count} doc${stepFlaggedStatus[step].count > 1 ? "s" : ""} to fix`
                                : "Action needed"}
                            </span>
                          )
                        )}
                      </div>
                      <h2 className="text-xl font-black leading-tight text-gray-900 sm:text-2xl">
                        {currentStep.id === 1
                          ? "Company Information"
                          : currentStep.id === 2
                            ? "Contact Person Details"
                            : currentStep.id === 3
                              ? "Business & Identity Verification"
                              : currentStep.id === 4
                                ? "Services, Capacity & Leads"
                                : currentStep.id === 5
                                  ? "Commercial & Sponsorship Model"
                                  : "Declaration & Consent"}
                      </h2>
                      <p className="mt-1 text-sm text-gray-400">{currentStep.subtitle}</p>
                    </div>
                    <div className={`w-11 h-11 rounded-2xl hidden sm:flex items-center justify-center shrink-0 ${
                      isFlaggedResubmissionFlow && stepFlaggedStatus[step]?.type === "approved"
                        ? "bg-emerald-100"
                        : isFlaggedResubmissionFlow && stepFlaggedStatus[step]?.type !== "approved"
                          ? "bg-amber-100"
                          : "bg-[#FFD221]/20"
                    }`}>
                      {React.createElement(currentStep.icon, {
                        size: 20,
                        className: isFlaggedResubmissionFlow && stepFlaggedStatus[step]?.type === "approved"
                          ? "text-emerald-600"
                          : isFlaggedResubmissionFlow && stepFlaggedStatus[step]?.type !== "approved"
                            ? "text-amber-600"
                            : "text-[#9a7b00]",
                      })}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Form body — same transition as header */}
              <div className="overflow-hidden flex-1 px-7 py-7 sm:px-8">
                <AnimatePresence mode="wait" custom={dir}>
                  <motion.div
                    key={step}
                    custom={dir}
                    variants={pageVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                  >
                    {currentStepLocked ? (
                      <div className="overflow-hidden mb-5 bg-gradient-to-r from-emerald-50 to-white rounded-2xl border border-emerald-200">
                        <div className="flex items-center gap-3 px-4 py-3.5">
                          <div className="flex justify-center items-center w-9 h-9 bg-emerald-100 rounded-xl shrink-0">
                            <CheckCircle2 size={18} className="text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-black text-emerald-800">Verified & Approved</p>
                            <p className="text-xs text-emerald-700/80 mt-0.5">
                              This section was reviewed and approved. Fields are read-only.{" "}
                              <Link to="/contact" className="font-bold underline underline-offset-2 hover:text-emerald-900">
                                Contact us
                              </Link>{" "}
                              to request a change.
                            </p>
                          </div>
                          <Lock size={16} className="text-emerald-400 shrink-0" />
                        </div>
                      </div>
                    ) : null}
                    <fieldset disabled={currentStepLocked} className={currentStepLocked ? "opacity-80 pointer-events-none" : ""}>
                      <StepComponent
                        form={form}
                        update={update}
                        errors={errors}
                        editableDocIds={flaggedEditableDocIds}
                        flaggedReason={flaggedContext.flagReason}
                        docStatuses={flaggedContext.documentStatuses || {}}
                        phoneCheck={phoneCheck}
                        emailCheck={emailCheck}
                        onPhoneChange={handlePhoneChange}
                        onEmailChange={handleEmailChange}
                      />
                    </fieldset>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Form footer */}
              <div className="flex flex-col gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50/50 sm:flex-row sm:items-end sm:justify-between sm:gap-4 sm:px-8 sm:py-5">
                <button
                  type="button"
                  onClick={prev}
                  disabled={step === 1}
                  className="inline-flex gap-2 justify-center items-center px-5 w-full h-11 text-sm font-semibold text-gray-600 bg-white rounded-xl border border-gray-200 transition-all hover:border-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-35 sm:w-auto"
                >
                  <ArrowLeft size={14} />
                  Previous
                </button>

                <div className="flex flex-wrap gap-2 justify-end items-end w-full sm:w-auto sm:flex-nowrap sm:gap-3">
                  {/* Step dots — desktop only */}
                  <div className="hidden xl:flex gap-1.5 mr-1">
                    {STEPS.map((s) => (
                      <div key={s.id}
                        className={`rounded-full transition-all duration-300 ${
                          s.id === step ? "w-3 h-1.5 bg-[#FFD221]" :
                          s.id < step  ? "w-1.5 h-1.5 bg-[#FFD221]/50" :
                          "w-1.5 h-1.5 bg-gray-200"
                        }`}
                      />
                    ))}
                  </div>

                  {/* Inline Save Draft — step 1+ once company basics are complete; hidden on last step / when fully complete */}
                  {!isFullFormComplete && isStep1Complete && step !== STEPS.length && (
                    <motion.button
                      type="button"
                      onClick={saveDraft}
                      disabled={isSavingDraft}
                      className={`inline-flex h-11 shrink-0 items-center gap-2 rounded-xl border px-4 sm:px-5 text-sm font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${
                        draftSaved
                          ? "border-[#FFD221]/60 bg-[#FFD221]/15 text-[#9a7b00]"
                          : "border-gray-200 bg-white text-gray-600 hover:border-[#FFD221]/60 hover:text-[#9a7b00] hover:bg-[#FFD221]/10"
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {draftSaved ? <CheckCircle2 size={13} /> : <Save size={13} />}
                      <span>{isSavingDraft ? "Saving…" : draftSaved ? "Saved!" : "Save draft"}</span>
                    </motion.button>
                  )}

                  {step === STEPS.length ? (
                    <div className="flex flex-col gap-2 items-stretch w-full shrink-0 sm:w-auto sm:items-end">
                      {!isFullFormComplete && (
                        <div className="w-full sm:w-[420px] rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3">
                          <div className="flex gap-3 justify-between items-start">
                            <div className="min-w-0">
                              <p className="text-[11px] font-black uppercase tracking-wider text-amber-700">
                                Missing required fields
                              </p>
                              <p className="mt-1 text-xs text-amber-700/80">
                                Add the items below to enable submission.
                              </p>
                            </div>
                            {fullValidation.firstInvalidStep && fullValidation.firstInvalidStep !== step && (
                              <button
                                type="button"
                                onClick={() => setStep(fullValidation.firstInvalidStep)}
                                className="shrink-0 text-[11px] font-bold text-amber-700 hover:underline"
                              >
                                Go to step {fullValidation.firstInvalidStep}
                              </button>
                            )}
                          </div>

                          <div className="mt-2 space-y-1">
                            {missingSubmitItems.slice(0, 6).map((it) => (
                              <div key={it.key} className="flex gap-2 items-start">
                                <span className="mt-[3px] w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                                <p className="text-xs leading-snug text-amber-800">
                                  <span className="font-bold">{it.label}</span>
                                  {it.msg ? <span className="text-amber-700/80"> — {it.msg}</span> : null}
                                </p>
                              </div>
                            ))}
                            {missingSubmitItems.length > 6 && (
                              <p className="text-[11px] text-amber-700/80 mt-1">
                                +{missingSubmitItems.length - 6} more
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {isFlaggedResubmissionFlow && flaggedCorrectionBlockers.length > 0 && (
                        <p className="text-left text-[11px] font-semibold text-amber-700 sm:text-right">
                          {flaggedCorrectionBlockers.length} correction item{flaggedCorrectionBlockers.length > 1 ? "s" : ""} remaining before re-submission.
                        </p>
                      )}
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        {SHOW_FINAL_PAYLOAD_BUTTON && (
                          <button
                            type="button"
                            onClick={showFinalPayload}
                            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-bold text-gray-700 transition hover:border-[#FFD221]/60 hover:bg-[#FFD221]/10 hover:text-[#9a7b00]"
                          >
                            <FileText size={14} />
                            View final payload
                          </button>
                        )}
                        <motion.button
                          type="button"
                          onClick={submit}
                          disabled={isSubmitting || !canSubmitFinal}
                          title={submitDisabledReason}
                          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#FFD221] px-5 sm:px-7 text-sm font-bold text-black transition-all shadow-lg shadow-[#FFD221]/30 hover:bg-[#c9a000] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#FFD221]"
                          whileHover={canSubmitFinal && !isSubmitting ? { scale: 1.02, y: -1 } : {}}
                          whileTap={canSubmitFinal && !isSubmitting ? { scale: 0.97 } : {}}
                        >
                          <Sparkles size={14} />
                          {isSubmitting
                            ? "Submitting…"
                            : isFlaggedResubmissionFlow
                              ? canSubmitFinal
                                ? "Re-submit Application"
                                : "Complete corrections to re-submit"
                              : "Submit Application"}
                        </motion.button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex w-full shrink-0 flex-col items-stretch gap-1.5 sm:w-auto sm:items-end">
                      {shouldWarnStepCompletion && (
                        <p className="whitespace-nowrap text-left text-[11px] font-semibold text-amber-700 sm:text-right">
                          Complete required fields or clear this step before continuing.
                        </p>
                      )}
                      <div className="flex flex-col-reverse gap-2 w-full sm:w-auto sm:flex-row sm:items-center">
                        {stepStarted && !currentStepLocked && !isFlaggedResubmissionFlow && (
                          <button
                            type="button"
                            onClick={clearCurrentStep}
                            className="inline-flex justify-center items-center px-4 w-full h-11 text-sm font-semibold text-amber-800 bg-amber-50 rounded-xl border border-amber-300 transition hover:bg-amber-100 sm:w-auto"
                          >
                            Clear step inputs
                          </button>
                        )}
                        <motion.button
                          type="button"
                          onClick={next}
                          title={shouldWarnStepCompletion ? "Complete required fields or clear this step to continue." : undefined}
                          className={`inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold transition-all shadow-lg sm:w-auto sm:px-7 ${
                            shouldWarnStepCompletion
                              ? "bg-amber-600 hover:bg-amber-700 text-white shadow-amber-500/20"
                              : "bg-[#FFD221] hover:bg-[#c9a000] text-black shadow-[#FFD221]/30"
                          }`}
                          whileHover={{ scale: 1.02, y: -1 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          <span className="truncate">Continue to {nextStep?.label || "Next step"}</span>
                          <ArrowRight size={14} />
                        </motion.button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <p className="mt-4 text-xs text-center text-gray-400">
              {isFullFormComplete
                ? isFlaggedResubmissionFlow
                  ? "All required corrections are complete — ready to re-submit."
                  : "All required fields are complete — ready to submit."
                : isStep1Complete
                  ? "A recovery code will be emailed to you when you save a draft."
                  : "Complete Step 1 to unlock draft saving and receive a recovery code."}
            </p>
          </div>

        </div>
      </div>
      <ActionStatusModal
        open={actionModal.open}
        status={actionModal.status}
        title={actionModal.title}
        message={actionModal.message}
        details={actionModal.details}
        code={actionModal.code}
        steps={actionModal.steps}
        onClose={closeActionModal}
      />

      {SHOW_FINAL_PAYLOAD_BUTTON && (
        <PayloadPreviewModal
          open={payloadPreview.open}
          payloadText={payloadPreview.text}
          onClose={() => setPayloadPreview({ open: false, text: "" })}
        />
      )}

      <LoadingOverlay
        open={isSavingDraft || isSubmitting}
        label={isSavingDraft ? "Saving draft…" : "Submitting your application…"}
      />
    </div>
  );
};

export default PartnerOnboardPage;
